import { createMessageHelper, EVENTS, ACTIONS } from 'griffith-message';
export * from 'griffith-message';
import { StyleSheet, css } from 'aphrodite/no-important';
import { ua, isMSESupported, isHlsNativeSupported, logger } from 'griffith-utils';
import BigScreen from 'isomorphic-bigscreen';
import React, { useContext, useMemo, useRef, useLayoutEffect, useState, useEffect, useCallback, cloneElement, createContext, Component } from 'react';
import EventEmitter from 'eventemitter3';
import debounce from 'lodash/debounce';
import memoize from 'lodash/memoize';
import clamp from 'lodash/clamp';
import noop from 'lodash/noop';
import GriffithHls from 'griffith-hls';
import GriffithMp4 from 'griffith-mp4';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

// 提供一个基础值，再推断出类型，让其他语言符合条件
const en = {
    'quality-auto': 'Auto',
    'quality-ld': 'LD',
    'quality-sd': 'SD',
    'quality-hd': 'HD',
    'quality-fhd': 'FHD',
    'action-enter-fullscreen': 'Fullscreen',
    'action-exit-fullscreen': 'Exit Fullscreen',
    'action-enter-page-fullscreen': 'Page Fullscreen',
    'action-exit-page-fullscreen': 'Exit Page Fullscreen',
    'action-enter-pip': 'Picture in Picture',
    'action-exit-pip': 'Exit Picture in Picture',
    'playback-rate': 'Speed',
    replay: 'Replay',
    play: 'Play',
    pause: 'Pause',
    mute: 'Mute',
    unmute: 'Unmute',
};
const defaultLocale = 'en';
// TODO: 应该提供 tree-shaking 的方式加载
const locales = {
    en,
    ja: {
        'quality-auto': '自動',
        'quality-ld': '低画質',
        'quality-sd': '標準画質',
        'quality-hd': '高画質',
        'quality-fhd': '超高画質',
        'action-enter-fullscreen': '全画面',
        'action-exit-fullscreen': '全画面終了',
        'action-enter-page-fullscreen': '全画面ページ',
        'action-exit-page-fullscreen': '全画面から撤退する',
        'action-enter-pip': 'ピクチャーインピクチャー',
        'action-exit-pip': 'ピクチャーインピクチャー終了',
        'playback-rate': '再生速度',
        replay: 'もう一回見る',
        play: '再生',
        pause: '一時停止',
        mute: 'ミュート（消音）',
        unmute: 'ミュート解除',
    },
    // covers zh-Hans-CN and zh-Hans-SG
    'zh-Hans': {
        'quality-auto': '自动',
        'quality-ld': '低清',
        'quality-sd': '标清',
        'quality-hd': '高清',
        'quality-fhd': '超清',
        'action-enter-fullscreen': '全屏',
        'action-exit-fullscreen': '退出全屏',
        'action-enter-page-fullscreen': '网页全屏',
        'action-exit-page-fullscreen': '退出网页全屏',
        'action-enter-pip': '画中画',
        'action-exit-pip': '退出画中画',
        'playback-rate': '倍速',
        replay: '重新播放',
        play: '播放',
        pause: '暂停',
        mute: '静音',
        unmute: '取消静音',
    },
    // covers zh-Hant-HK and zh-Hant-TW
    'zh-Hant': {
        'quality-auto': '自動',
        'quality-ld': '低畫質',
        'quality-sd': '標準畫質',
        'quality-hd': '高畫質',
        'quality-fhd': '超高畫質',
        'action-enter-fullscreen': '全螢幕',
        'action-exit-fullscreen': '結束全螢幕',
        'action-enter-page-fullscreen': '網頁全屏',
        'action-exit-page-fullscreen': '退出網頁全屏',
        'action-enter-pip': '子母畫面',
        'action-exit-pip': '關閉子母畫面',
        'playback-rate': '倍速',
        replay: '重新播放',
        play: '播放',
        pause: '暫停',
        mute: '静音',
        unmute: '解除静音',
    },
};

const LocaleContext = React.createContext(locales[defaultLocale]);
LocaleContext.displayName = 'LocaleContext';
/** Get i18n locale text */
const useLocaleText = (name, style = 'text') => {
    const locale = useContext(LocaleContext);
    if (typeof locale[name] === 'object') {
        const value = locale[name];
        return value[style];
    }
    return locale[name];
};

const getLocalConfig = (locale, userLocals) => {
    const defaultConfig = locales[locale];
    const userConfig = userLocals === null || userLocals === void 0 ? void 0 : userLocals[locale];
    return userConfig
        ? Object.assign({}, defaultConfig, userConfig)
        : defaultConfig;
};
const LocaleProvider = ({ locale, localeConfig, children }) => {
    const value = useMemo(() => getLocalConfig(locale, localeConfig), [locale, localeConfig]);
    return (React.createElement(LocaleContext.Provider, { value: value }, children));
};

/**
 * Create immutable function ref
 */
const useHandler = (handler) => {
    const handlerRef = useRef(handler);
    handlerRef.current = handler;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return useRef(((...args) => handlerRef.current(...args))).current;
};

const EVENT_TYPE = 'event';
const ACTION_TYPE = 'action';
/**
 * 用于播放器内部，只能接收外界传入的 Action，向外界发出 Event
 */
const InternalMessageContext = React.createContext({});
InternalMessageContext.displayName = 'InternalMessageContext';
/**
 * 用于播放器外部，只能接收播放器发出的 Event, 或者向播放器发送 Action
 */
const MessageContext = React.createContext(null);
MessageContext.displayName = 'MessageContext';
/**
 *
 * Retrieve `MessageContext` from outside of Player
 *
 * ```js
 * const messageContextRef = useMessageContextRef()
 *
 * messageContextRef.useEvent(EVENTS.PLAY, () => {})
 *
 * render(
 *  <>
 *    <Player messageContextRef={messageContextRef} />
 *    <button onClick={() => messageContextRef.dispatchAction(ACTIONS.PLAY)}>Play</button>
 *  </>
 * )
 * ```
 */
const useMessageContextRef = () => {
    const extra = {
        useEvent(name, listener) {
            const handler = useHandler(listener);
            useLayoutEffect(() => ref.subscribeEvent(name, handler).unsubscribe, [name, handler]);
        },
    };
    const ref = useRef(extra).current;
    // Player mount 中触发了事件，确保在它之前开始监听
    useLayoutEffect(() => {
        if (!ref.current) {
            throw new Error('Missing ref value, please pass it to Player, eg. `<Player messageContextRef={messageContextRef} />`');
        }
        Object.assign(ref, ref.current);
    }, [ref]);
    return ref;
};
class MessageProvider extends React.PureComponent {
    constructor(props) {
        super(props);
        this.emitEvent = (eventName, data) => {
            var _a, _b;
            this.emitter.emit(eventName, { __type__: EVENT_TYPE, data });
            (_b = (_a = this.props).onEvent) === null || _b === void 0 ? void 0 : _b.call(_a, eventName, data);
            if (this.props.enableCrossWindow) {
                this.crossWindowMessager.dispatchMessage(window.parent, eventName, data);
            }
        };
        this.subscribeEvent = (eventName, listener) => {
            const realListener = ({ __type__, data } = {}) => {
                if (__type__ === EVENT_TYPE) {
                    // @ts-expect-error Argument of type 'any' is not assignable to parameter of type 'never'
                    listener(data);
                }
            };
            this.emitter.on(eventName, realListener);
            return {
                unsubscribe: () => this.emitter.off(eventName, realListener),
            };
        };
        this.dispatchAction = (actionName, data) => {
            this.emitter.emit(actionName, { __type__: ACTION_TYPE, data });
        };
        this.subscribeAction = (actionName, listener) => {
            const realListener = ({ __type__, data }) => {
                if (__type__ === ACTION_TYPE) {
                    listener(data);
                }
            };
            this.emitter.on(actionName, realListener);
            return {
                unsubscribe: () => this.emitter.off(actionName, realListener),
            };
        };
        this.internalContextValue = {
            emitEvent: this.emitEvent,
            subscribeAction: this.subscribeAction,
        };
        this.externalContextValue = {
            dispatchAction: this.dispatchAction,
            subscribeEvent: this.subscribeEvent,
        };
        this.emitter = new EventEmitter();
        const { id, targetOrigin } = this.props;
        this.crossWindowMessager = createMessageHelper(id, targetOrigin);
        if (this.props.dispatchRef) {
            this.props.dispatchRef.current = this.externalContextValue.dispatchAction;
        }
        if (this.props.messageContextRef) {
            this.props.messageContextRef.current = this.externalContextValue;
        }
        void Promise.resolve().then(() => {
            this.emitEvent(EVENTS.SUBSCRIPTION_READY);
        });
    }
    componentDidMount() {
        if (this.props.enableCrossWindow) {
            this.crossWindowMessager.subscribeMessage((name, data) => {
                // TODO: `createMessageHelper` 应当在 API 上区分内部使用（event/action 与相反）与外部使用，这会导致签名不匹配
                this.dispatchAction(name, data);
            });
        }
    }
    componentWillUnmount() {
        if (this.props.dispatchRef) {
            this.props.dispatchRef.current = undefined;
        }
        if (this.props.messageContextRef) {
            this.props.messageContextRef.current = undefined;
        }
        this.crossWindowMessager.dispose();
        this.emitter.removeAllListeners();
    }
    render() {
        return (React.createElement(InternalMessageContext.Provider, { value: this.internalContextValue },
            React.createElement(MessageContext.Provider, { value: this.externalContextValue }, this.props.children)));
    }
}
MessageProvider.defaultProps = {
    targetOrigin: '*',
};

const ObjectFitContext = React.createContext({});
ObjectFitContext.displayName = 'ObjectFitContext';

const ObjectFitProvider = ({ initialObjectFit = 'contain', children, }) => {
    const [objectFit, setObjectFit] = useState(initialObjectFit);
    const contextValue = useMemo(() => ({
        objectFit,
        setObjectFit,
    }), [objectFit, setObjectFit]);
    useEffect(() => {
        setObjectFit(initialObjectFit);
    }, [initialObjectFit]);
    return (React.createElement(ObjectFitContext.Provider, { value: contextValue }, children));
};

/**
 * Get previous rendered value
 *
 * @see https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
 */
function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}

const listenResizePolyfill = (target, callback) => {
    const MutationObserver = window.MutationObserver || window.WebKitMutationObserver;
    let lastRect;
    const handler = () => {
        const rect = target.getBoundingClientRect();
        const keys = ['left', 'top', 'width', 'height'];
        const isChanged = !(lastRect && keys.every((k) => lastRect[k] === rect[k]));
        if (isChanged) {
            lastRect = rect;
            callback(rect);
        }
    };
    const debounceHandler = debounce(handler, 100);
    const observer = new MutationObserver(debounceHandler);
    observer.observe(target, {
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true,
    });
    handler();
    window.addEventListener('resize', handler);
    return () => {
        observer.disconnect();
        window.removeEventListener('resize', handler);
    };
};
const listenResizeNative = (target, callback) => {
    const observer = new ResizeObserver((changes) => callback(changes[0].contentRect));
    observer.observe(target);
    return () => {
        observer.disconnect();
    };
};
/** 监听元素大小改改 @see https://caniuse.com/#search=ResizeObserver */
const listenResize = typeof ResizeObserver === 'function'
    ? listenResizeNative
    : listenResizePolyfill;

const PositionContext = React.createContext({
    isFullWidth: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateVideoSize() { },
});
PositionContext.displayName = 'PositionContext';

var styles$a = StyleSheet.create({
    root: {
        position: 'relative',
        width: '100%',
        height: '100%',
    },
});

// Create a placeholder image to preserve the original aspect ratio
const createHolderImageSrc = (width, height) => `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}'></svg>`;
const PositionProvider = ({ children }) => {
    const ref = useRef(null);
    const [helperImageSrc, setHelperImageSrc] = useState(null);
    const [isFullWidth, setIsFullWidth] = useState(false);
    const [videoSize, setVideoSize] = useState({
        videoWidth: 0,
        videoHeight: 0,
    });
    const updateHelperImageSrc = useHandler(() => {
        const { videoWidth, videoHeight } = videoSize;
        if (!videoWidth || !videoHeight) {
            return;
        }
        setHelperImageSrc(createHolderImageSrc(videoWidth, videoHeight));
    });
    const updateIsFullWidth = useHandler(() => {
        const { videoWidth, videoHeight } = videoSize;
        if (!videoWidth || !videoHeight) {
            return;
        }
        if (!ref.current) {
            return;
        }
        const { width, height } = ref.current.getBoundingClientRect();
        // 因为视频缩放后，长宽可能不严格相等，所以认为差值小于等于 0.01 的就算相等。
        // 比如 1280x720 (1.777777778) 和 848x478 (1.774058577)，认为相等。
        const isFullWidthNew = width / height - videoWidth / videoHeight <= 0.01;
        if (isFullWidthNew !== isFullWidth) {
            setIsFullWidth(isFullWidthNew);
        }
    });
    useEffect(() => {
        if (ref.current) {
            return listenResize(ref.current, updateIsFullWidth);
        }
    }, [ref, updateIsFullWidth]);
    useEffect(() => {
        updateHelperImageSrc();
    }, [updateHelperImageSrc]);
    const prevVideoSize = usePrevious(videoSize);
    useEffect(() => {
        if (!prevVideoSize) {
            return;
        }
        const { videoWidth: prevWidth, videoHeight: prevHeight } = prevVideoSize;
        const { videoWidth, videoHeight } = videoSize;
        if (prevWidth !== videoWidth || prevHeight !== videoHeight) {
            requestAnimationFrame(updateIsFullWidth);
            updateHelperImageSrc();
        }
    }, [prevVideoSize, updateHelperImageSrc, updateIsFullWidth, videoSize]);
    const contextValue = useMemo(() => ({ isFullWidth, helperImageSrc, updateVideoSize: setVideoSize }), [helperImageSrc, isFullWidth]);
    return (React.createElement(PositionContext.Provider, { value: contextValue },
        React.createElement("div", { className: css(styles$a.root), ref: ref }, children)));
};

const VideoSourceContext = React.createContext({});
VideoSourceContext.displayName = 'VideoSourceContext';

const QUALITY_ORDER = ['auto', 'ld', 'sd', 'hd', 'fhd'];
const getQualities = (sources, isMobile, isDescOrder) => {
    const qualities = Object.keys(sources).sort((a, b) => isDescOrder
        ? QUALITY_ORDER.indexOf(b) - QUALITY_ORDER.indexOf(a)
        : QUALITY_ORDER.indexOf(a) - QUALITY_ORDER.indexOf(b));
    if (qualities.length > 1) {
        if (isMobile) {
            // 移动端只返回最低清晰度
            return qualities.slice(0, 1);
        }
        else {
            // 桌面端端去掉低清，除非只有一个低清
            return qualities.filter((item) => item !== 'ld');
        }
    }
    return qualities;
};
const getSources = (qualities, sources) => qualities.map((quality) => {
    // @ts-expect-error Property 'auto' does not exist on type 'PlaySourceMap'，应当是 QUALITY_ORDER 定义错了
    const _a = sources[quality], { play_url } = _a, rest = __rest(_a, ["play_url"]);
    return Object.assign(Object.assign({}, rest), { source: play_url, quality });
});

/**
 * reverse an array without mutating the original one
 */
var reverseArray = (arr) => arr.slice().reverse();

/**
 * Get last changed value (useful for object comparison)
 */
const useChanged = (value) => {
    const ref = useRef(value);
    if (value !== ref.current &&
        JSON.stringify(value) !== JSON.stringify(ref.current)) {
        ref.current = value;
    }
    return ref.current;
};

const { isMobile: isMobile$1 } = ua;
const VideoSourceProvider = ({ sources: sourceMap, useAutoQuality, playbackRates, defaultPlaybackRate, defaultQuality, defaultQualityOrder, children, }) => {
    const { emitEvent } = useContext(InternalMessageContext);
    const lastSourceMap = useChanged(sourceMap);
    const { qualities, sources, format, isDescOrder } = useMemo(() => {
        // 其实视频源应当是必需参数
        if (!lastSourceMap) {
            return { qualities: [], sources: [] };
        }
        const { format } = Object.values(lastSourceMap)[0];
        const isDescOrder = defaultQualityOrder === 'desc';
        const qualities = getQualities(lastSourceMap, isMobile$1, isDescOrder);
        const sources = getSources(qualities, lastSourceMap);
        // 目前只有直播流实现了手动拼接 auto 清晰度的功能
        if (useAutoQuality &&
            !isMobile$1 &&
            format === 'm3u8' &&
            !qualities.includes('auto')) {
            qualities.unshift('auto');
        }
        return { qualities, sources, format, isDescOrder };
    }, [useAutoQuality, lastSourceMap, defaultQualityOrder]);
    const [currentQuality, setCurrentQualityRaw] = useState(defaultQuality && qualities.includes(defaultQuality)
        ? defaultQuality
        : qualities[0]);
    const [playbackRate, setPlaybackRate] = useState(defaultPlaybackRate);
    const setCurrentQuality = useHandler((quality) => {
        if (currentQuality !== quality) {
            setCurrentQualityRaw(quality);
            emitEvent(EVENTS.QUALITY_CHANGE, {
                prevQuality: currentQuality,
                quality,
            });
        }
    });
    // 当 sources 改变重置 `currentQuality`
    const prevQualities = usePrevious(qualities);
    useEffect(() => {
        if (prevQualities && prevQualities !== qualities) {
            setCurrentQuality(defaultQuality || qualities[0]);
        }
    }, [prevQualities, qualities, defaultQuality, setCurrentQuality]);
    const setCurrentPlaybackRate = useHandler((rate) => {
        if (playbackRate !== rate) {
            setPlaybackRate(rate);
            emitEvent(EVENTS.PLAYBACK_RATE_CHANGE, {
                prevRate: playbackRate,
                rate,
            });
        }
    });
    // 根据当前清晰度返回对应的 src
    const currentSrc = useMemo(() => {
        if (sources.length === 0) {
            return;
        }
        const source = sources.find((item) => item.quality === currentQuality) || sources[0];
        return source.source;
    }, [currentQuality, sources]);
    const contextValue = useMemo(() => ({
        qualities: isDescOrder ? qualities : reverseArray(qualities),
        playbackRates,
        format: format,
        sources,
        currentQuality,
        currentPlaybackRate: playbackRate,
        currentSrc: currentSrc,
        setCurrentQuality,
        setCurrentPlaybackRate,
    }), [
        playbackRate,
        currentQuality,
        currentSrc,
        format,
        playbackRates,
        qualities,
        isDescOrder,
        setCurrentPlaybackRate,
        setCurrentQuality,
        sources,
    ]);
    return (React.createElement(VideoSourceContext.Provider, { value: contextValue }, children));
};

/**
 * Create a boolean value and switches for changing it
 *
 * ```js
 * const [isOpen, isOpenSwitch] = useBoolean()
 *
 * <Button onClick={isOpenSwitch.on} label="Open" />
 * <Button onClick={isOpenSwitch.off} label="Close" />
 * <Button onClick={isOpenSwitch.toggle} label="Toggle" />
 * ```
 */
const useBoolean = (initialState = false) => {
    const [value, setValue] = useState(initialState);
    const valueSwitch = useRef({
        on: useCallback(() => setValue(true), []),
        off: useCallback(() => setValue(false), []),
        toggle: useCallback(() => setValue((v) => !v), []),
    }).current;
    return [value, valueSwitch];
};

/**
 * Mount callback
 */
function useMount(effect) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(effect, []);
}

// https://github.com/zloirock/core-js/issues/280
function pad(number) {
    let result = String(number);
    if (result.length < 2) {
        result = '0' + result;
    }
    return result;
}
function formatDuration(time) {
    if (!Number.isFinite(time))
        return '';
    time = Math.floor(time);
    const seconds = time % 60;
    const minutes = Math.floor(time / 60);
    return `${pad(minutes)}:${pad(seconds)}`;
}

function getBufferedTime(currentTime, buffered) {
    const item = buffered.find((item) => item.start <= currentTime && currentTime <= item.end);
    if (item) {
        return item.end;
    }
    return 0;
}

// Refer to:
// https://w3c.github.io/picture-in-picture/
// https://css-tricks.com/an-introduction-to-the-picture-in-picture-web-api/
class Pip {
    constructor() {
        this.inited = false;
    }
    init(element, onEnter, onExit) {
        this.inited = true;
        this.element = element;
        this.onEnter = onEnter;
        this.onExit = onExit;
        this.element.addEventListener('enterpictureinpicture', this.handleEnter.bind(this));
        this.element.addEventListener('leavepictureinpicture', this.handleExit.bind(this));
    }
    handleEnter(e) {
        if (this.onEnter) {
            this.onEnter(e);
        }
    }
    handleExit(e) {
        if (this.onExit) {
            this.onExit(e);
        }
    }
    get supported() {
        if (!this.inited)
            return false;
        // experimental types
        return document.pictureInPictureEnabled;
    }
    get pictureInPictureElement() {
        return document.pictureInPictureElement;
    }
    request() {
        if (this.element) {
            this.element.requestPictureInPicture();
        }
    }
    exit() {
        document.exitPictureInPicture();
    }
    toggle() {
        if (this.pictureInPictureElement) {
            this.exit();
        }
        else {
            this.request();
        }
    }
}

const compatibleStorage = {
    store: {},
    setItem(key, value) {
        this.store[key] = value;
    },
    removeItem(key) {
        return delete this.store[key];
    },
    getItem(key) {
        return this.store[key];
    },
};
const storage = {
    isSupported: memoize(() => {
        try {
            const testSupport = {
                key: '__test_support_key__',
                value: '__test_support_value__',
            };
            const supported = Boolean(global.localStorage);
            localStorage.setItem(testSupport.key, testSupport.value);
            localStorage.removeItem(testSupport.key);
            return supported;
        }
        catch (err) {
            return false;
        }
    }),
    getStorage: memoize(() => storage.isSupported() ? localStorage : compatibleStorage),
    set(key, value) {
        storage.getStorage().setItem(key, JSON.stringify(value));
    },
    get(key) {
        try {
            const value = storage.getStorage().getItem(key);
            return JSON.parse(value);
        }
        catch (error) {
            return null;
        }
    },
    delete(key) {
        storage.getStorage().removeItem(key);
    },
};

const actionAnimation = {
    '0%': {
        opacity: 0,
        transform: 'scale(0)',
    },
    '50%': {
        opacity: 0.6,
        transform: 'scale(0.8)',
    },
    '100%': {
        opacity: 0,
        transform: 'scale(1)',
    },
};
const actionLabelAnimation = {
    '90%': {
        opacity: 1,
    },
    '100%': {
        opacity: 0,
    },
};
const fadeinAnimation = {
    '0%': {
        opacity: 0,
    },
    '100%': {
        opacity: 1,
    },
};
const breakpoints = {
    mobile: '26em', // 416px
};
const breakTo = (breakpoint) => `@media screen and (max-width: ${breakpoints[breakpoint]})`;
var styles$9 = StyleSheet.create({
    root: {
        position: 'relative',
        overflow: 'hidden',
        userSelect: 'none',
        width: '100%',
        height: '100%',
        fontSize: 16,
        backgroundColor: '#000',
        ':focus': {
            outline: 'none',
        },
    },
    fullScreened: {
        fontSize: 24,
    },
    pageFullScreen: {
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 100000,
    },
    actionButton: {
        color: '#fff',
        width: '4.5em',
        height: '4.5em',
    },
    actionButtonAnimated: {
        animationName: actionAnimation,
        animationDuration: '600ms',
        animationFillMode: 'both',
    },
    actionLabelAnimation: {
        animationName: actionLabelAnimation,
        animationDuration: '600ms',
        animationFillMode: 'both',
    },
    actionIcon: {
        width: '4.5em',
        height: '4.5em',
        [breakTo('mobile')]: {
            transform: 'scale(0.75)',
        },
    },
    actionLabel: {
        marginTop: '.5em',
        padding: '.3em .5em',
        textAlign: 'center',
        color: '#fff',
        background: 'rgba(0,0,0,0.5)',
        borderRadius: 5,
    },
    video: {
        width: '100%',
        height: '100%',
    },
    overlay: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        overflow: 'hidden',
    },
    overlayMask: {
        background: 'rgba(0, 0, 0, 0.08)',
    },
    loader: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        color: '#0f88eb',
        transform: 'translate(-50%, -50%)',
    },
    action: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
    },
    actionToast: {
        pointerEvents: 'none',
    },
    backdrop: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
    },
    title: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        overflow: 'hidden',
        padding: '0 1em',
        lineHeight: 2.5,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: '#fff',
        backgroundImage: 'linear-gradient(to top, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.3))',
        transform: 'translateY(-100%)',
        transition: 'transform 300ms',
    },
    titleShown: {
        transform: 'translateY(0)',
    },
    controller: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.3))',
    },
    volumeButton: {
        position: 'absolute',
        bottom: 0,
        right: 8,
    },
    cover: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        overflow: 'hidden',
        backgroundColor: '#000',
        cursor: 'pointer',
        visibility: 'hidden',
        opacity: 0,
        transition: 'visibility 300ms, opacity 300ms',
    },
    coverShown: {
        visibility: 'visible',
        opacity: 1,
    },
    coverImage: {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
    },
    coverTime: {
        position: 'absolute',
        right: '0.5em',
        bottom: '0.5em',
        padding: '0.375em',
        fontSize: '0.875em',
        lineHeight: 1,
        color: 'rgba(255, 255, 255, 0.9)',
        backgroundColor: 'transparent',
        textShadow: '0 0 6px rgba(0, 0, 0, 0.4)',
    },
    coverTimeMobile: {
        padding: 0,
    },
    coverAction: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
    },
    coverReplayAction: {
        position: 'absolute',
        left: '0',
        right: '0',
        top: '0',
        bottom: '0',
        backgroundColor: 'rgba(0, 0, 0, 0.16)',
        animationName: fadeinAnimation,
        animationDuration: '600ms',
        animationFillMode: 'both',
    },
    coverReplayButton: {
        position: 'absolute',
        left: '0',
        right: '0',
        top: '0',
        bottom: '0',
        height: '40px',
        width: '116px',
        margin: 'auto',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        borderRadius: '4px',
        color: '#fff',
        fontSize: '16px',
        lineHeight: '24px',
        padding: '8px',
        boxSizing: 'border-box',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        transition: 'transform 200ms, opacity 200ms',
        willChange: 'transform',
    },
    coverReplayButtonHovered: {
        transform: 'scale(1.1)',
    },
    coverReplayButtonPressed: {
        opacity: 0.7,
    },
    replayIcon: {
        verticalAlign: 'top',
        marginRight: '4px',
    },
    error: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        color: '#fff',
        backgroundColor: '#000',
    },
    errorIcon: {
        width: '2.5em',
        height: '2.5em',
        margin: '0 auto',
        fill: 'currentColor',
    },
    errorMessage: {
        marginTop: '1em',
        fontSize: '1.25em',
        lineHeight: 1,
        textAlign: 'center',
    },
});
const hiddenOrShownStyle = StyleSheet.create({
    base: {
        transition: `opacity .5s ease-in-out, visibility .5s ease-in-out`,
    },
    hidden: {
        opacity: 0,
        visibility: 'hidden',
    },
    shown: {
        opacity: 1,
        visibility: 'visible',
    },
});

var styles$8 = StyleSheet.create({
    root: {
        display: 'inline-block',
        width: '1.5em',
        height: '1.5em',
    },
    svg: {
        display: 'block',
        width: '100%',
        height: '100%',
        fill: 'currentColor',
    },
});

function Icon({ icon, styles: customStyles }) {
    const children = cloneElement(icon, {
        className: css(styles$8.svg),
    });
    return React.createElement("span", { className: css(styles$8.root, customStyles) }, children);
}

const ActionToastStateContext = createContext(void 0);
const ActionToastDispatchContext = createContext(() => {
    // noop
});
/** 触发操作反馈 */
const useActionToastDispatch = () => useContext(ActionToastDispatchContext);
/**
 * 操作反馈提示（主要提示用户热键或鼠标操作的结果）
 */
const ActionToastProvider = ({ children }) => {
    const [state, setState] = useState();
    const lastKey = useRef(0);
    const dispatch = useCallback((state) => {
        // 每一次设定必定是创建一个新的提示，key 的变化让元素重新 mount，CSS 动画得以执行
        lastKey.current += 1;
        setState(Object.assign(Object.assign({}, state), { key: String(lastKey.current) }));
    }, []);
    return (React.createElement(ActionToastStateContext.Provider, { value: state },
        React.createElement(ActionToastDispatchContext.Provider, { value: dispatch }, children)));
};
/** 操作反馈提示的目标渲染位置 */
const ActionToastOutlet = React.memo(() => {
    const state = useContext(ActionToastStateContext);
    if (!state) {
        return null;
    }
    const { key, icon, label } = state;
    return (React.createElement("div", { className: css(styles$9.action, styles$9.actionToast), key: key },
        React.createElement("div", { className: css(styles$9.actionButton, styles$9.actionButtonAnimated) }, icon && React.createElement(Icon, { icon: icon, styles: styles$9.actionIcon })),
        label && (React.createElement("div", { className: css(styles$9.actionLabel, styles$9.actionLabelAnimation) }, label))));
});

var exitPip = (React.createElement("svg", { viewBox: "0 0 24 24" },
    React.createElement("path", { d: "M19.7180574,15 C20.1638168,15 20.3254599,15.0464128 20.4884229,15.1335664 C20.6513858,15.2207199 20.7792801,15.3486142 20.8664336,15.5115771 C20.9535872,15.6745401 21,15.8361832 21,16.2819426 L21,19.7180574 C21,20.1638168 20.9535872,20.3254599 20.8664336,20.4884229 C20.7792801,20.6513858 20.6513858,20.7792801 20.4884229,20.8664336 C20.3254599,20.9535872 20.1638168,21 19.7180574,21 L16.2819426,21 C15.8361832,21 15.6745401,20.9535872 15.5115771,20.8664336 C15.3486142,20.7792801 15.2207199,20.6513858 15.1335664,20.4884229 C15.0464128,20.3254599 15,20.1638168 15,19.7180574 L15,16.2819426 C15,15.8361832 15.0464128,15.6745401 15.1335664,15.5115771 C15.2207199,15.3486142 15.3486142,15.2207199 15.5115771,15.1335664 C15.6745401,15.0464128 15.8361832,15 16.2819426,15 L19.7180574,15 Z M18.5,4 C19.3284271,4 20,4.67157288 20,5.5 L20,11.75 C20,11.8880712 19.8880712,12 19.75,12 L18.25,12 C18.1119288,12 18,11.8880712 18,11.75 L18,6.5 C18,6.24358208 17.8069799,6.03224642 17.5583106,6.00336387 L17.5,6 L6.5,6 C6.24358208,6 6.03224642,6.1930201 6.00336387,6.44168944 L6,6.5 L6,17.5 C6,17.7564179 6.1930201,17.9677536 6.44168944,17.9966361 L6.5,18 L11.75,18 C11.8880712,18 12,18.1119288 12,18.25 L12,19.75 C12,19.8880712 11.8880712,20 11.75,20 L5.5,20 C4.67157288,20 4,19.3284271 4,18.5 L4,5.5 C4,4.67157288 4.67157288,4 5.5,4 L18.5,4 Z M12.263324,13.6777091 L9.53508966,10.956021 L9.55615206,13.2225752 C9.55502994,13.3470231 9.46566931,13.4482773 9.34885743,13.4683645 L9.30388492,13.4719793 L7.79658322,13.4583417 C7.67542934,13.4572455 7.57490537,13.3702653 7.5531943,13.2554476 L7.54886064,13.2111995 L7.49923911,7.74790313 C7.50035209,7.62546574 7.5897117,7.52443494 7.70652348,7.50437256 L7.75149597,7.50076089 L13.2230869,7.55026617 C13.3442408,7.55136234 13.4447648,7.6383426 13.4664758,7.75316028 L13.4708095,7.79740841 L13.4844791,9.30570263 C13.4833661,9.42814002 13.3940065,9.52917082 13.2771947,9.5492332 L13.2322222,9.55284487 L10.9362217,9.53206969 L13.6775376,12.2668086 C13.7751687,12.364211 13.7751687,12.5221314 13.6775376,12.6195338 L12.6168774,13.6777091 C12.5192463,13.7751115 12.3609551,13.7751115 12.263324,13.6777091 Z" })));

var fullscreen = (React.createElement("svg", { viewBox: "0 0 24 24" },
    React.createElement("path", { d: "M20,10 L20,5 C20,4.44771525 19.5490248,4 19.009222,4 L14,4 L14,6 L18,6 L18,10 L20,10 Z M19.7115371,19.7068284 C19.8902571,19.5277733 20,19.2813055 20,19.009222 L20,14 L18,14 L18,18 L14,18 L14,20 L19.009222,20 C19.2776249,20 19.5240668,19.8893106 19.7040291,19.7101145 L19.7115371,19.7068284 Z M6,18 L6,14 L4,14 L4,19.009222 C4,19.2813055 4.10974288,19.5277733 4.28846285,19.7068284 C4.47593315,19.8893106 4.72237506,20 4.99077797,20 L10,20 L10,18 L6,18 Z M4.28846285,4.29317155 C4.10974288,4.47222673 4,4.71869448 4,4.99077797 L4,10 L6,10 L6,6 L10,6 L10,4 L4.99077797,4 C4.72237506,4 4.47593315,4.11068941 4.29597088,4.28988548 L4.28846285,4.29317155 Z" })));

var muted = (React.createElement("svg", { viewBox: "0 0 24 24" },
    React.createElement("path", { d: "M7.38001507,9.5 L5,9.5 L5,12.0119396 L5,14.5238791 L8.33098415,14.5238791 L8.33098415,14.5238791 C8.39017333,14.520451 8.4497727,14.5223174 8.50904598,14.5296109 C8.72475585,14.5524598 8.91816198,14.6424266 9.06920843,14.7779841 L13,17.4293365 L13,15.1199849 L7.38001507,9.5 Z M15,17.1199849 L15,19.251289 C15,19.8045373 14.5522847,20.25 14,20.25 C13.98423,20.25 13.9685502,20.2496405 13.9529683,20.2489292 C13.7472793,20.2584936 13.5372091,20.2041245 13.3535806,20.0802655 L8.08102098,16.5238791 L4.00000001,16.5238791 C3.71994909,16.5238791 3.46994918,16.4119504 3.29196292,16.2310404 C3.11117675,16.0497507 3,15.7991447 3,15.5223056 L3,12.0119396 L3,8.50157356 C3,8.2247344 3.11117675,7.97412842 3.29196292,7.79283868 C3.46994918,7.61192872 3.71994909,7.5 4.00000001,7.5 L5.38001507,7.5 L3.38382011,5.50380504 C2.99233448,5.1123194 2.98883249,4.48109777 3.38207944,4.08785081 C3.77260374,3.69732652 4.3999302,3.691488 4.79803367,4.08959148 L18.936688,18.2282458 C19.3281736,18.6197314 19.3316756,19.250953 18.9384286,19.6442 C18.5479043,20.0347243 17.9205779,20.0405628 17.5224744,19.6424593 L15,17.1199849 Z M10.792179,8.08373676 L9.35149599,6.64305379 L13.3535806,3.94361361 C13.5372091,3.81975463 13.7472793,3.76538554 13.9529683,3.77494994 C13.9685502,3.77423867 13.98423,3.77387913 14,3.77387913 C14.5522847,3.77387913 15,4.21934183 15,4.77259008 L15,12.0119396 L15,12.2915578 L13,10.2915578 L13,6.59454265 L10.792179,8.08373676 Z M18.0173138,15.3088716 C18.8917509,14.408489 19.4301566,13.1799808 19.4301566,11.8257212 C19.4301566,10.2214787 18.6746368,8.79369726 17.5,7.87877967 L17.5067574,7.87009164 C17.2041341,7.69816923 17,7.37292366 17,7 C17,6.44771525 17.4477153,6 18,6 C18.2836948,6 18.5397977,6.11813499 18.721799,6.30789526 L18.7279374,6.30000305 C20.3724289,7.58088767 21.4301566,9.57978168 21.4301566,11.8257212 C21.4301566,13.7322659 20.6679517,15.4607882 19.431636,16.7231938 L18.0173138,15.3088716 Z" })));

var pause$1 = (React.createElement("svg", { viewBox: "0 0 24 24" },
    React.createElement("path", { d: "M9,5 C10,5 10,6 10,6 C10,6 10,17 10,18 C10,19 9,19 9,19 L7,19 C6,19 6,18 6,18 L6,6 C6,5 7,5 7,5 C7,5 8,5 9,5 Z M17,5 C18,5 18,6 18,6 C18,6 18,17 18,18 C18,19 17,19 17,19 L15,19 C14,19 14,18 14,18 L14,6 C14,5 15,5 15,5 C15,5 16,5 17,5 Z" })));

var pip = (React.createElement("svg", { viewBox: "0 0 24 24" },
    React.createElement("path", { d: "M19.7180574,15 C20.1638168,15 20.3254599,15.0464128 20.4884229,15.1335664 C20.6513858,15.2207199 20.7792801,15.3486142 20.8664336,15.5115771 C20.9535872,15.6745401 21,15.8361832 21,16.2819426 L21,19.7180574 C21,20.1638168 20.9535872,20.3254599 20.8664336,20.4884229 C20.7792801,20.6513858 20.6513858,20.7792801 20.4884229,20.8664336 C20.3254599,20.9535872 20.1638168,21 19.7180574,21 L16.2819426,21 C15.8361832,21 15.6745401,20.9535872 15.5115771,20.8664336 C15.3486142,20.7792801 15.2207199,20.6513858 15.1335664,20.4884229 C15.0464128,20.3254599 15,20.1638168 15,19.7180574 L15,16.2819426 C15,15.8361832 15.0464128,15.6745401 15.1335664,15.5115771 C15.2207199,15.3486142 15.3486142,15.2207199 15.5115771,15.1335664 C15.6745401,15.0464128 15.8361832,15 16.2819426,15 L19.7180574,15 Z M18.5,4 C19.3284271,4 20,4.67157288 20,5.5 L20,11.75 C20,11.8880712 19.8880712,12 19.75,12 L18.25,12 C18.1119288,12 18,11.8880712 18,11.75 L18,6.5 C18,6.24358208 17.8069799,6.03224642 17.5583106,6.00336387 L17.5,6 L6.5,6 C6.24358208,6 6.03224642,6.1930201 6.00336387,6.44168944 L6,6.5 L6,17.5 C6,17.7564179 6.1930201,17.9677536 6.44168944,17.9966361 L6.5,18 L11.75,18 C11.8880712,18 12,18.1119288 12,18.25 L12,19.75 C12,19.8880712 11.8880712,20 11.75,20 L5.5,20 C4.67157288,20 4,19.3284271 4,18.5 L4,5.5 C4,4.67157288 4.67157288,4 5.5,4 L18.5,4 Z M8.98667598,7.57381266 L11.7149103,10.2955008 L11.6938479,8.02894655 C11.6949701,7.90449864 11.7843307,7.80324445 11.9011426,7.78315725 L11.9461151,7.77954248 L13.4534168,7.79318008 C13.5745707,7.79427625 13.6750946,7.88125651 13.6968057,7.99607419 L13.7011394,8.04032232 L13.7507609,13.5036187 C13.7496479,13.626056 13.6602883,13.7270868 13.5434765,13.7471492 L13.498504,13.7507609 L8.0269131,13.7012556 C7.90575922,13.7001594 7.80523525,13.6131792 7.78352418,13.4983615 L7.77919052,13.4541134 L7.76552089,11.9458192 C7.76663388,11.8233818 7.85599349,11.722351 7.97280526,11.7022886 L8.01777775,11.6986769 L10.3137783,11.7194521 L7.57246241,8.98471315 C7.47483134,8.8873108 7.47483134,8.72939038 7.57246241,8.63198803 L8.63312259,7.57381266 C8.73075366,7.4764103 8.8890449,7.4764103 8.98667598,7.57381266 Z" })));

var play$1 = (React.createElement("svg", { viewBox: "0 0 24 24" },
    React.createElement("path", { d: "M8.03610236,19.7012935 C6.93281899,20.3993842 6,19.8347521 6,18.4154722 L6,5.59575582 C6,4.14567779 6.91151094,3.59901121 8.03610236,4.30993442 L18.1645278,10.7185093 C19.2678112,11.4166 19.2891193,12.5817954 18.1645278,13.2927186 L8.03610236,19.7012935 Z" })));

var smallscreen = (React.createElement("svg", { viewBox: "0 0 24 24" },
    React.createElement("path", { d: "M14.2884629,14.2931716 C14.1097429,14.4722267 14,14.7186945 14,14.990778 L14,20 L16,20 L16,16 L20,16 L20,14 L14.990778,14 C14.7223751,14 14.4759332,14.1106894 14.2959709,14.2898855 L14.2884629,14.2931716 Z M16,8 L16,4 L14,4 L14,9.00922203 C14,9.28130552 14.1097429,9.52777327 14.2884629,9.70682845 C14.4759332,9.88931059 14.7223751,10 14.990778,10 L20,10 L20,8 L16,8 Z M8,16 L8,20 L10,20 L10,14.990778 C10,14.7186945 9.89025712,14.4722267 9.71153715,14.2931716 C9.52406685,14.1106894 9.27762494,14 9.00922203,14 L4,14 L4,16 L8,16 L8,16 Z M9.71153715,9.70682845 C9.89025712,9.52777327 10,9.28130552 10,9.00922203 L10,4 L8,4 L8,8 L4,8 L4,10 L9.00922203,10 C9.27762494,10 9.52406685,9.88931059 9.70402912,9.71011452 L9.71153715,9.70682845 Z" })));

var volume = (React.createElement("svg", { viewBox: "0 0 24 24" },
    React.createElement("path", { d: "M5,12.0119396 L5,14.5238791 L8.33098415,14.5238791 L8.33098415,14.5238791 C8.39017333,14.520451 8.4497727,14.5223174 8.50904598,14.5296109 C8.72475585,14.5524598 8.91816198,14.6424266 9.06920843,14.7779841 L13,17.4293365 L13,12.0119396 L13,6.59454265 L9.06920843,9.24589504 C8.91816198,9.38145255 8.72475585,9.47141933 8.50904598,9.49426822 C8.4497727,9.50156175 8.39017333,9.5034281 8.33098415,9.5 L8.33098415,9.5 L5,9.5 L5,12.0119396 Z M3,12.0119396 L3,8.50157356 C3,8.2247344 3.11117675,7.97412842 3.29196292,7.79283868 C3.46994918,7.61192872 3.71994909,7.5 4.00000001,7.5 L8.08102098,7.5 L13.3535806,3.94361361 C13.5372091,3.81975463 13.7472793,3.76538554 13.9529683,3.77494994 C13.9685502,3.77423867 13.98423,3.77387913 14,3.77387913 C14.5522847,3.77387913 15,4.21934183 15,4.77259008 L15,12.0119396 L15,19.251289 C15,19.8045373 14.5522847,20.25 14,20.25 C13.98423,20.25 13.9685502,20.2496405 13.9529683,20.2489292 C13.7472793,20.2584936 13.5372091,20.2041245 13.3535806,20.0802655 L8.08102098,16.5238791 L4.00000001,16.5238791 C3.71994909,16.5238791 3.46994918,16.4119504 3.29196292,16.2310404 C3.11117675,16.0497507 3,15.7991447 3,15.5223056 L3,12.0119396 Z M17.5059844,15.7803571 L17.5,15.7726628 C18.6746368,14.8577452 19.4301566,13.4299638 19.4301566,11.8257212 C19.4301566,10.2214787 18.6746368,8.79369726 17.5,7.87877967 L17.5067574,7.87009164 C17.2041341,7.69816923 17,7.37292366 17,7 C17,6.44771525 17.4477153,6 18,6 C18.2836948,6 18.5397977,6.11813499 18.721799,6.30789526 L18.7279374,6.30000305 C20.3724289,7.58088767 21.4301566,9.57978168 21.4301566,11.8257212 C21.4301566,14.0716608 20.3724289,16.0705548 18.7279374,17.3514394 L18.7211832,17.3427554 C18.5392202,17.5321404 18.283375,17.6500092 18,17.6500092 C17.4477153,17.6500092 17,17.2022939 17,16.6500092 C17,16.2774031 17.2037866,15.9523939 17.5059844,15.7803571 L17.5059844,15.7803571 Z" })));

var enterPageScreen = (React.createElement("svg", { viewBox: "0 -4 16 24" },
    React.createElement("path", { d: "M15.9964 0.207284C15.9763 0.0904726 15.8753 0.00111298 15.7529 0L10.2896 0.0496215L10.2453 0.0539552C10.1305 0.0756663 10.0435 0.17619 10.0424 0.297344L10.0288 1.80465L10.0324 1.84962C10.0525 1.96643 10.1537 2.05579 10.2782 2.05691L12.5447 2.03585L9.82305 4.76408C9.72565 4.86172 9.72565 5.02001 9.82305 5.11764L10.8812 6.1783C10.9786 6.27593 11.1365 6.27593 11.234 6.1783L13.9687 3.43698L13.9479 5.73298L13.9515 5.77796C13.9716 5.89477 14.0726 5.98413 14.1951 5.98524L15.7034 5.97157L15.7476 5.96724C15.8624 5.94553 15.9494 5.845 15.9505 5.72385L16 0.252257L15.9964 0.207284ZM0.00361156 15.7942C0.0236739 15.911 0.124705 16.0004 0.247142 16.0015L5.71044 15.9519L5.75469 15.9476C5.8695 15.9259 5.95648 15.8253 5.95758 15.7042L5.97122 14.1969L5.9676 14.1519C5.94752 14.0351 5.84626 13.9457 5.72181 13.9446L3.45526 13.9657L6.17695 11.2374C6.27435 11.1398 6.27435 10.9815 6.17695 10.8839L5.11877 9.82322C5.02137 9.72559 4.86345 9.72559 4.76605 9.82322L2.03131 12.5645L2.05208 10.2685L2.04847 10.2236C2.02841 10.1068 1.92738 10.0174 1.80494 10.0163L0.296648 10.03L0.252399 10.0343C0.137582 10.056 0.0506014 10.1565 0.0495052 10.2777L0 15.7493L0.00361156 15.7942Z" })));

var exitPageScreen = (React.createElement("svg", { viewBox: "0 -4 16 24" },
    React.createElement("path", { d: "M9.75361 6.04424C9.77367 6.16105 9.87471 6.25041 9.99714 6.25152L15.4604 6.2019L15.5047 6.19757C15.6195 6.17586 15.7065 6.07533 15.7076 5.95418L15.7212 4.44688L15.7176 4.4019C15.6975 4.28509 15.5963 4.19573 15.4718 4.19461L13.2053 4.21567L15.9269 1.48744C16.0244 1.38981 16.0244 1.23151 15.9269 1.13388L14.8688 0.0732233C14.7714 -0.0244078 14.6135 -0.0244078 14.516 0.0732233L11.7813 2.81454L11.8021 0.518539L11.7985 0.473566C11.7784 0.356754 11.6774 0.267395 11.5549 0.266282L10.0466 0.279951L10.0024 0.284285C9.88758 0.305996 9.8006 0.40652 9.79951 0.527674L9.75 5.99926L9.75361 6.04424ZM6.24639 9.95728C6.22633 9.84047 6.1253 9.75111 6.00286 9.75L0.539561 9.79962L0.495313 9.80396C0.380496 9.82567 0.293515 9.92619 0.292419 10.0473L0.278782 11.5546L0.282396 11.5996C0.302484 11.7164 0.403738 11.8058 0.528186 11.8069L2.79474 11.7859L0.0730518 14.5141C-0.0243506 14.6117 -0.0243506 14.77 0.0730518 14.8676L1.13123 15.9283C1.22863 16.0259 1.38655 16.0259 1.48395 15.9283L4.21869 13.187L4.19792 15.483L4.20153 15.528C4.22159 15.6448 4.32262 15.7341 4.44506 15.7352L5.95335 15.7216L5.9976 15.7172C6.11242 15.6955 6.1994 15.595 6.20049 15.4738L6.25 10.0023L6.24639 9.95728Z" })));

var styles$7 = StyleSheet.create({
    root: {
        padding: '0 0.5em',
    },
    rootTop: {
        height: '2em',
    },
    rootBottom: {
        height: '2.5em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    rootBottomLeft: {
        display: 'flex',
    },
    rootBottomRight: {
        display: 'flex',
    },
    button: {
        display: 'block',
        width: '2.5em',
        height: '2.5em',
        margin: 0,
        padding: '0.5em',
        border: 'none',
        borderRadius: 0,
        fontSize: 'inherit',
        color: 'rgba(255, 255, 255, 0.7)',
        background: 'none',
        cursor: 'pointer',
        transition: 'color 300ms',
        ':hover': {
            color: '#fff',
        },
        ':focus': {
            outline: 'none',
        },
    },
    menuContainer: {
        position: 'relative',
    },
    menu: {
        position: 'absolute',
        left: '50%',
        bottom: '100%',
        visibility: 'hidden',
        opacity: 0,
        transform: 'translateX(-50%)',
        transition: 'visibility 300ms, opacity 300ms',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        borderRadius: '4px',
    },
    menuShown: {
        visibility: 'visible',
        opacity: 1,
    },
    timeline: {
        flex: 1,
    },
    timelineHolder: {
        flex: 1,
    },
    time: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 0.5em',
        minWidth: '100px',
        fontSize: '0.875em',
        color: 'rgba(255, 255, 255, 0.9)',
        boxSizing: 'content-box',
        fontVariantNumeric: 'tabular-nums',
    },
    labelButton: {
        width: 'auto',
        lineHeight: 1.5,
    },
    labelButtonText: {
        fontSize: '0.875em',
    },
    qualityMenu: {
        padding: '0.25em 0',
    },
    qualityMenuItem: {
        display: 'block',
        width: '100%',
        boxSizing: 'border-box',
        height: '2em',
        margin: 0,
        padding: '0 0.75em',
        border: 'none',
        borderRadius: 0,
        fontSize: '0.875em',
        lineHeight: 2,
        whiteSpace: 'nowrap',
        color: 'rgba(255, 255, 255, 0.5)',
        background: 'none',
        cursor: 'pointer',
        transition: 'color 300ms, background-color 300ms',
        ':hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
        ':focus': {
            outline: 'none',
        },
    },
    qualityMenuActiveItem: {
        color: '#fff',
    },
    fullScreenedTime: {
        width: '150px',
    },
    tooltipContent: {
        pointerEvents: 'none',
        padding: '0.25em 0.5em',
        fontSize: '0.875em',
        whiteSpace: 'nowrap',
        color: '#fff',
    },
});

const ControllerButton = (_a) => {
    var { icon, label } = _a, props = __rest(_a, ["icon", "label"]);
    return (React.createElement("button", Object.assign({}, props, { className: css(styles$7.button, label ? styles$7.labelButton : null), type: "button" }),
        icon && React.createElement(Icon, { icon: icon }),
        label && React.createElement("span", { className: css(styles$7.labelButtonText) }, label)));
};

const canUseTouch = typeof document !== 'undefined' && 'ontouchstart' in document.documentElement;
/**
 * ControllerButton
 *
 * `localeKey` 取本地文本，显示为 tooltip，同时设定为 children 的 `aria-label`
 */
const ControllerTooltip = ({ localeKey, hotkey, children }) => {
    const text = useLocaleText(localeKey);
    const [isHovered, isHoveredSwitch] = useBoolean();
    return (React.createElement("div", { className: css(styles$7.menuContainer), onMouseEnter: isHoveredSwitch.on, onMouseLeave: isHoveredSwitch.off },
        cloneElement(children, { 'aria-label': text }),
        !canUseTouch && (React.createElement("div", { className: css(styles$7.menu, isHovered && styles$7.menuShown, styles$7.tooltipContent) },
            text,
            hotkey && ` (${hotkey})`))));
};

const PlayButtonItem = ({ isPlaying, onClick }) => (React.createElement(ControllerTooltip, { localeKey: isPlaying ? 'pause' : 'play', hotkey: "k" },
    React.createElement(ControllerButton, { icon: isPlaying ? pause$1 : play$1, onClick: onClick })));

function calculatePercentage(value, total) {
    if (!total) {
        return `0%`;
    }
    return `${clamp(value / total, 0, 1) * 100}%`;
}

var styles$6 = StyleSheet.create({
    root: {
        position: 'absolute',
        display: 'flex',
        top: 0,
        bottom: 0,
        width: '100%',
    },
    item: {
        position: 'absolute',
        width: 12,
        height: '100%',
        transform: 'translate(-50%)',
        ':hover': {
            height: 6,
            top: -1,
        },
    },
    innerItem: {
        width: 6,
        backgroundColor: '#ff9607',
        height: '100%',
        margin: '0 auto',
    },
});

const ProgressDotItem = ({ startTime, total, onProgressDotHover, onProgressDotLeave, }) => {
    const ref = useRef(null);
    const handleMouseEnter = () => {
        if (!ref.current) {
            return;
        }
        const { left, top } = ref.current.getBoundingClientRect();
        onProgressDotHover === null || onProgressDotHover === void 0 ? void 0 : onProgressDotHover({ startTime, left, top });
    };
    return (React.createElement("div", { className: css(styles$6.item), style: {
            left: calculatePercentage(startTime, total),
        }, onMouseEnter: handleMouseEnter, onMouseLeave: onProgressDotLeave },
        React.createElement("div", { ref: ref, className: css(styles$6.innerItem) })));
};
const ProgressDots = ({ progressDots = [], total, onProgressDotHover, onProgressDotLeave, }) => {
    return (React.createElement("div", { className: css(styles$6.root) }, progressDots.map((i, index) => (React.createElement(ProgressDotItem, { key: index, startTime: i.startTime, total: total, onProgressDotHover: onProgressDotHover, onProgressDotLeave: onProgressDotLeave })))));
};

var styles$5 = StyleSheet.create({
    root: {
        overflow: 'hidden',
        userSelect: 'none',
        cursor: 'pointer',
        touchAction: 'none',
        WebkitTapHighlightColor: 'transparent',
        ':focus': {
            outline: 'none',
        },
    },
    inner: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '100%',
    },
    track: {
        position: 'relative',
        flex: 1,
        overflow: 'hidden',
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '2px',
    },
    bar: {
        position: 'absolute',
        backgroundColor: '#fff',
        borderRadius: '2px 0 0 2px',
    },
    thumbWrapper: {
        position: 'absolute',
    },
    thumb: {
        width: '1em',
        height: '1em',
        margin: '-0.5em',
        borderRadius: '50%',
        backgroundColor: '#fff',
    },
});
const horizontal = StyleSheet.create({
    root: {
        height: '1em',
        padding: `0 0.5em`,
    },
    inner: {
        flexDirection: 'row',
    },
    track: {
        height: 4,
    },
    bar: {
        top: 0,
        bottom: 0,
    },
    thumbWrapper: {
        top: '50%',
    },
});
const vertical = StyleSheet.create({
    root: {
        width: '1em',
        padding: `0.5em 0`,
    },
    inner: {
        flexDirection: 'column',
    },
    track: {
        width: 4,
    },
    bar: {
        left: 0,
        right: 0,
    },
    thumbWrapper: {
        left: '50%',
    },
});

const useSetState = (initialState = {}) => {
    const [state, setState] = useState(initialState);
    const setPartial = useCallback((nextState) => setState((prevState) => (Object.assign(Object.assign({}, prevState), nextState))), []);
    return [state, setPartial];
};

const getRatio = (value, total) => total ? clamp(value / total, 0, 1) : 0;
const toPercentage = (value) => `${value * 100}%`;
Slider.defaultProps = {
    orientation: 'horizontal',
    reverse: false,
    progressDots: [],
};
function Slider(props) {
    const { orientation, reverse, onFocus, onBlur, onChange, onDragStart, onDragEnd, noInteraction, progressDots, value = 0, buffered = 0, total = 0, step = 1, onProgressDotHover, onProgressDotLeave, } = props;
    const [{ isSlideActive, isSliding, slidingValue }, setState] = useSetState({
        isSlideActive: false,
        isSliding: false,
        slidingValue: null,
    });
    const isHorizontal = orientation === 'horizontal';
    const trackRef = useRef(null);
    const getStyles = (name) => {
        const variantStyles = isHorizontal ? horizontal : vertical;
        let customStyles = props.styles;
        if (!Array.isArray(customStyles)) {
            customStyles = [customStyles];
        }
        customStyles = customStyles.filter(Boolean);
        return [
            styles$5[name],
            variantStyles[name],
            ...customStyles.map((item) => item[name]),
        ];
    };
    const getClassName = (...names) => {
        return css(...names.map((name) => getStyles(name)));
    };
    const alignKey = (() => {
        if (isHorizontal) {
            return reverse ? 'right' : 'left';
        }
        else {
            return reverse ? 'top' : 'bottom';
        }
    })();
    const sizeKey = isHorizontal ? 'width' : 'height';
    const getProgressStyle = (value) => {
        const scaleAxis = isHorizontal ? 'scaleX' : 'scaleY';
        return {
            [sizeKey]: '100%',
            transform: `${scaleAxis}(${value})`,
            transformOrigin: alignKey,
        };
    };
    const getProgressThumbStyle = (value) => {
        const translateAxis = isHorizontal ? 'translateX' : 'translateY';
        return {
            [sizeKey]: '100%',
            transform: `${translateAxis}(${toPercentage(isHorizontal ? value : 1 - value)})`,
            transformOrigin: alignKey,
        };
    };
    const getSlidingValue = (event) => {
        const track = trackRef.current;
        if (!track)
            return 0;
        const rect = track.getBoundingClientRect();
        let value;
        if (isHorizontal) {
            value = (event.clientX - rect.left) / rect.width;
        }
        else {
            value = (rect.bottom - event.clientY) / rect.height;
        }
        value = clamp(value, 0, 1);
        if (reverse) {
            value = 1 - value;
        }
        return value * total;
    };
    const handleKeyDown = (event) => {
        let direction = 0;
        let handled = false;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
            handled = true;
            direction = -1;
        }
        else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
            handled = true;
            direction = 1;
        }
        if (handled) {
            event.preventDefault();
            if (reverse) {
                direction = -direction;
            }
            const result = clamp(value + step * direction, 0, total);
            if (result !== value) {
                handleChange(result);
            }
        }
    };
    const handleDragStart = (event) => {
        if (event.button !== 0)
            return;
        const value = getSlidingValue(event);
        setState({
            isSlideActive: true,
            slidingValue: value,
        });
        onDragStart === null || onDragStart === void 0 ? void 0 : onDragStart();
        handleChange(value);
    };
    const handleDragMove = useHandler((event) => {
        const value = getSlidingValue(event);
        setState({
            slidingValue: value,
            isSliding: true,
        });
        handleChange(value);
    });
    const handleDragEnd = useHandler((event) => {
        onDragEnd === null || onDragEnd === void 0 ? void 0 : onDragEnd();
        if (isSliding) {
            // 点击动作不需要重复触发 change event
            handleChange(getSlidingValue(event));
        }
        setState({
            isSlideActive: false,
            slidingValue: null,
            isSliding: false,
        });
    });
    useEffect(() => {
        if (isSlideActive) {
            document.addEventListener('mousemove', handleDragMove);
            document.addEventListener('mouseup', handleDragEnd);
            return () => {
                document.removeEventListener('mousemove', handleDragMove);
                document.removeEventListener('mouseup', handleDragEnd);
            };
        }
    }, [handleDragEnd, handleDragMove, isSlideActive]);
    const handleChange = (value) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(value);
    };
    const interactionProps = noInteraction
        ? {}
        : {
            tabIndex: 0,
            onFocus,
            onBlur,
            onKeyDown: handleKeyDown,
            onMouseDown: handleDragStart,
        };
    const ratio = getRatio(isSlideActive ? slidingValue : value, total);
    const bufferedRatio = getRatio(buffered, total);
    return (React.createElement("div", Object.assign({ className: getClassName('root') }, interactionProps, { role: "slider" }),
        React.createElement("div", { className: getClassName('inner') },
            React.createElement("div", { ref: trackRef, className: getClassName('track') },
                Boolean(buffered) && (React.createElement("div", { className: getClassName('bar', 'buffered'), style: getProgressStyle(bufferedRatio) })),
                React.createElement("div", { className: getClassName('bar'), style: getProgressStyle(ratio) }),
                Boolean(progressDots === null || progressDots === void 0 ? void 0 : progressDots.length) && (React.createElement(ProgressDots, { progressDots: progressDots, total: total, onProgressDotHover: onProgressDotHover, onProgressDotLeave: onProgressDotLeave }))),
            !noInteraction && (
            // the position indicator (visible when hovering)
            React.createElement("div", { className: getClassName('thumbWrapper'), style: getProgressThumbStyle(ratio) },
                React.createElement("div", { className: getClassName('thumb', (isSlideActive && 'thumbSliding')) }))))));
}

var styles$4 = StyleSheet.create({
    root: {},
});
const slider$1 = StyleSheet.create({
    root: {},
    track: {
        transition: 'transform 300ms',
    },
    bar: {
        backgroundColor: '#0f88eb',
    },
    thumb: {
        transform: 'scale(0)',
        transition: 'transform 300ms',
    },
    buffered: {
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        borderRadius: '2px 0 0 2px',
    },
});
const hoveredSlider = StyleSheet.create({
    thumb: {
        transform: 'scale(1)',
    },
});
const dotHoveredSlider = StyleSheet.create({
    track: {
        overflow: 'visible',
    },
});
const minimal = StyleSheet.create({
    root: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: 0,
        height: '3px',
    },
    track: {
        borderRadius: 0,
    },
    thumb: {
        display: 'none',
    },
    buffered: {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '2px 0 0 2px',
    },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useConstCallbacks = (...fns) => 
// eslint-disable-next-line react-hooks/exhaustive-deps
useCallback((...args) => fns.forEach((f) => f(...args)), []);
const Timeline = (_a) => {
    var { value, onDragStart, onDragEnd, onSeek, onChange, onProgressDotHover, onProgressDotLeave } = _a, props = __rest(_a, ["value", "onDragStart", "onDragEnd", "onSeek", "onChange", "onProgressDotHover", "onProgressDotLeave"]);
    const [isFocused, isFocusedSwitch] = useBoolean();
    const [isHovered, isHoveredSwitch] = useBoolean();
    const [isDragging, isDraggingSwitch] = useBoolean();
    const [progressDotHovered, progressDotHoveredSwitch] = useBoolean();
    const handleChange = useHandler((newValue) => {
        if (newValue !== value) {
            onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
        }
        if (!isDragging) {
            onSeek === null || onSeek === void 0 ? void 0 : onSeek(newValue);
        }
    });
    return (React.createElement("div", { className: css(styles$4.root), onMouseEnter: isHoveredSwitch.on, onMouseLeave: useConstCallbacks(isHoveredSwitch.off, isFocusedSwitch.off) },
        React.createElement(Slider, Object.assign({}, props, { styles: [
                slider$1,
                (isHovered || isFocused || isDragging) && hoveredSlider,
                progressDotHovered && dotHoveredSlider,
            ].filter(Boolean), value: value, onChange: handleChange, onFocus: isFocusedSwitch.on, onBlur: isFocusedSwitch.off, onDragStart: useConstCallbacks(isDraggingSwitch.on, useHandler(onDragStart || noop)), onDragEnd: useConstCallbacks(isDraggingSwitch.off, useHandler(onDragEnd || noop)), onProgressDotHover: useConstCallbacks(progressDotHoveredSwitch.on, useHandler(onProgressDotHover || noop)), onProgressDotLeave: useConstCallbacks(progressDotHoveredSwitch.off, useHandler(onProgressDotLeave || noop)) }))));
};

const TimelineItem = (props) => {
    return (React.createElement("div", { className: css(styles$7.timeline) },
        React.createElement(Timeline, Object.assign({}, props))));
};

const CombinedTimeItem = ({ isFullScreen, currentTime, duration }) => (React.createElement("div", { className: css(styles$7.time, isFullScreen && styles$7.fullScreenedTime) }, `${formatDuration(currentTime)} / ${formatDuration(duration)}`));

const TranslatedText = ({ name, style = 'text' }) => {
    return React.createElement(React.Fragment, null, useLocaleText(name, style));
};

const QUALITIES = {
    auto: 'quality-auto',
    ld: 'quality-ld',
    sd: 'quality-sd',
    hd: 'quality-hd',
    fhd: 'quality-fhd',
};
class QualityMenuItem extends React.PureComponent {
    constructor() {
        super(...arguments);
        this.state = {
            isQualityHovered: false,
        };
        this.handleQualityPointerEnter = () => {
            this.setState({ isQualityHovered: true });
        };
        this.handleQualityPointerLeave = () => {
            this.setState({ isQualityHovered: false });
        };
    }
    render() {
        const { isQualityHovered } = this.state;
        return (React.createElement(VideoSourceContext.Consumer, null, ({ qualities, setCurrentQuality, currentQuality }) => (qualities === null || qualities === void 0 ? void 0 : qualities.length) > 1 && (React.createElement("div", { className: css(styles$7.menuContainer), onMouseEnter: this.handleQualityPointerEnter, onMouseLeave: this.handleQualityPointerLeave },
            React.createElement(ControllerButton, { label: React.createElement(TranslatedText, { style: "narrow", name: QUALITIES[currentQuality] }) }),
            React.createElement("div", { className: css(styles$7.menu, isQualityHovered && styles$7.menuShown) },
                React.createElement("div", { className: css(styles$7.qualityMenu) }, qualities.map((q) => (React.createElement("button", { key: q, className: css(styles$7.qualityMenuItem, currentQuality === q && styles$7.qualityMenuActiveItem), onClick: () => {
                        this.handleQualityPointerLeave();
                        setCurrentQuality(q);
                    } },
                    React.createElement(TranslatedText, { name: QUALITIES[q] }))))))))));
    }
}

var styles$3 = StyleSheet.create({
    root: {
        padding: '0.375em 0',
    },
});
const slider = StyleSheet.create({
    root: {
        width: '2.5em',
        height: '4em',
    },
    track: {
        margin: '-0.4em 0',
        // 之前是 -0.5em（thumb 的半径），是为了在 0 或 100% 的情况下，让 thumb 的边缘和 track 的边缘平齐
        // 因为下面缩放了 0.8 所以改为 0.5em * 0.8 = 0.4em
        // workaround for bug with overflow: hidden; in chrome
        transform: 'translate3d(0, 0, 0)',
    },
    thumb: {
        transform: 'scale(0.8)',
        transition: 'transform 300ms',
        ':hover': {
            transform: 'scale(1)',
        },
    },
    thumbSliding: {
        transform: 'scale(1)',
    },
});

const VolumeSlider = (props) => {
    return (React.createElement("div", { className: css(styles$3.root) },
        React.createElement(Slider, Object.assign({}, props, { styles: slider, orientation: "vertical", step: 0.1 }))));
};

const VolumeItem = ({ volume: volume$1, menuShown, onMouseEnter, onMouseLeave, onDragStart, onDragEnd, onChange, onToggleMuted, }) => (React.createElement("div", { className: css(styles$7.menuContainer), onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave },
    React.createElement(ControllerButton, { "aria-label": useLocaleText(volume$1 === 0 ? 'unmute' : 'mute'), icon: volume$1 === 0 ? muted : volume, onClick: () => onToggleMuted === null || onToggleMuted === void 0 ? void 0 : onToggleMuted() }),
    React.createElement("div", { className: css(styles$7.menu, menuShown && styles$7.menuShown) },
        React.createElement(VolumeSlider, { value: volume$1, total: 1, onDragStart: onDragStart, onDragEnd: onDragEnd, onChange: onChange }))));

const FullScreenButtonItem = ({ isFullScreen, onClick }) => (React.createElement(ControllerTooltip, { localeKey: isFullScreen ? 'action-exit-fullscreen' : 'action-enter-fullscreen', hotkey: "f" },
    React.createElement(ControllerButton, { icon: isFullScreen ? smallscreen : fullscreen, onClick: onClick })));

const PipButtonItem = ({ isPip, onClick }) => (React.createElement(ControllerTooltip, { localeKey: isPip ? 'action-exit-pip' : 'action-enter-pip', hotkey: "p" },
    React.createElement(ControllerButton, { icon: isPip ? exitPip : pip, onClick: onClick })));

class PlaybackRateMenuItem extends React.PureComponent {
    constructor() {
        super(...arguments);
        this.state = {
            isPlaybackRateHovered: false,
        };
        this.handlePlaybackRatePointerEnter = () => {
            this.setState({ isPlaybackRateHovered: true });
        };
        this.handlePlaybackRatePointerLeave = () => {
            this.setState({ isPlaybackRateHovered: false });
        };
    }
    render() {
        const { isPlaybackRateHovered } = this.state;
        return (React.createElement(VideoSourceContext.Consumer, null, ({ playbackRates, setCurrentPlaybackRate, currentPlaybackRate }) => (playbackRates === null || playbackRates === void 0 ? void 0 : playbackRates.length) > 1 && (React.createElement("div", { className: css(styles$7.menuContainer), onMouseEnter: this.handlePlaybackRatePointerEnter, onMouseLeave: this.handlePlaybackRatePointerLeave },
            React.createElement(ControllerButton, { label: currentPlaybackRate.value === 1.0 ? (React.createElement(TranslatedText, { name: 'playback-rate' })) : (`${currentPlaybackRate.text}`) }),
            React.createElement("div", { className: css(styles$7.menu, isPlaybackRateHovered && styles$7.menuShown) },
                React.createElement("div", { className: css(styles$7.qualityMenu) }, reverseArray(playbackRates).map((q) => (React.createElement("button", { key: q.value, className: css(styles$7.qualityMenuItem, currentPlaybackRate.value === q.value &&
                        styles$7.qualityMenuActiveItem), onClick: () => {
                        this.handlePlaybackRatePointerLeave();
                        setCurrentPlaybackRate(q);
                    } }, q.text)))))))));
    }
}

const PageFullScreenButtonItem = ({ isFullScreen, onClick }) => (React.createElement(ControllerTooltip, { localeKey: isFullScreen
        ? 'action-exit-page-fullscreen'
        : 'action-enter-page-fullscreen', hotkey: "t" },
    React.createElement(ControllerButton, { icon: isFullScreen ? exitPageScreen : enterPageScreen, onClick: onClick })));

Controller.defaultProps = {
    show: false,
    standalone: false,
    duration: 0,
    currentTime: 0,
    volume: 0.5,
    buffered: 0,
    isPageFullScreen: false,
    showPip: false,
    hiddenPlayButton: false,
    hiddenTimeline: false,
    hiddenTime: false,
    hiddenQualityMenu: false,
    hiddenPlaybackRateItem: false,
    hiddenVolumeItem: false,
    hiddenFullScreenButton: false,
    progressDots: [],
};
function Controller(props) {
    const { isPlaying = false, buffered, duration, currentTime, volume, isFullScreen = false, isPageFullScreen, isPip, onDragStart, onDragEnd, onToggleFullScreen, onTogglePageFullScreen, onTogglePip, showPip, progressDots, hiddenPlayButton, hiddenTimeline, hiddenTime, hiddenQualityMenu, hiddenVolumeItem, hiddenPlaybackRateItem, hiddenFullScreenButton, shouldShowPageFullScreenButton, onProgressDotHover, onProgressDotLeave, onTogglePlay, onSeek, onToggleMuted, onVolumeChange, } = props;
    const [isVolumeHovered, isVolumeHoveredSwitch] = useBoolean();
    const [slideTime, setSlideTime] = useState();
    const handleDragMove = useHandler((slideTime) => {
        setSlideTime(clamp(slideTime, 0, duration));
    });
    const handleSeek = useHandler((currentTime) => {
        onSeek === null || onSeek === void 0 ? void 0 : onSeek(clamp(currentTime, 0, duration));
        setSlideTime(void 0);
    });
    const displayedCurrentTime = slideTime || currentTime;
    return (React.createElement("div", { className: css(styles$7.root) },
        !hiddenTimeline && (React.createElement(TimelineItem, { value: currentTime, total: duration, buffered: buffered, progressDots: progressDots, onDragStart: onDragStart, onDragEnd: onDragEnd, onChange: handleDragMove, onSeek: handleSeek, onProgressDotHover: onProgressDotHover, onProgressDotLeave: onProgressDotLeave })),
        React.createElement("div", { className: css(styles$7.rootBottom) },
            React.createElement("div", { className: css(styles$7.rootBottomLeft) },
                !hiddenPlayButton && (React.createElement(PlayButtonItem, { isPlaying: isPlaying, onClick: () => onTogglePlay === null || onTogglePlay === void 0 ? void 0 : onTogglePlay() })),
                hiddenTimeline && React.createElement("div", { className: css(styles$7.timelineHolder) }),
                !hiddenTime && (React.createElement(CombinedTimeItem, { isFullScreen: isFullScreen, currentTime: displayedCurrentTime, duration: duration }))),
            React.createElement("div", { className: css(styles$7.rootBottomRight) },
                !hiddenPlaybackRateItem && React.createElement(PlaybackRateMenuItem, null),
                !hiddenQualityMenu && React.createElement(QualityMenuItem, null),
                showPip && React.createElement(PipButtonItem, { isPip: isPip, onClick: onTogglePip }),
                shouldShowPageFullScreenButton && (React.createElement(PageFullScreenButtonItem, { isFullScreen: isPageFullScreen, onClick: onTogglePageFullScreen })),
                !hiddenFullScreenButton && (React.createElement(FullScreenButtonItem, { isFullScreen: isFullScreen, onClick: onToggleFullScreen })),
                !hiddenVolumeItem && (React.createElement(VolumeItem, { volume: volume, menuShown: isVolumeHovered, onMouseEnter: isVolumeHoveredSwitch.on, onMouseLeave: isVolumeHoveredSwitch.off, onToggleMuted: onToggleMuted, onChange: onVolumeChange }))))));
}
var Controller$1 = React.memo(Controller);

var alert = (React.createElement("svg", { viewBox: "0 0 20 20" },
    React.createElement("path", { d: "M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm-.034 2.966c1.95 0 1.485 1.807 1.485 2.21 0 .116-.784 7.79-.784 7.79h-1.4S8.48 5.344 8.48 5.23c0-.417-.462-2.264 1.486-2.264zm1.057 13.522c-.294.287-.647.43-1.056.43-.41 0-.762-.142-1.058-.43-.296-.286-.444-.643-.444-1.07 0-.41.148-.762.443-1.057.294-.294.647-.44 1.057-.44.41 0 .762.142 1.056.43.296.286.443.643.443 1.07 0 .426-.147.782-.443 1.068z" })));

var pause = (React.createElement("svg", { viewBox: "0 0 72 72" },
    React.createElement("g", { fill: "none", fillRule: "evenodd" },
        React.createElement("circle", { cx: "36", cy: "36", r: "36", fill: "#FFF", fillOpacity: ".95" }),
        React.createElement("path", { fill: "#444", fillRule: "nonzero", d: "M41.143 22.5h5.786v27h-5.786zM25.071 22.5h5.786v27h-5.786z" }))));

var play = (React.createElement("svg", { viewBox: "0 0 72 72" },
    React.createElement("g", { fill: "none", fillRule: "evenodd" },
        React.createElement("circle", { cx: "36", cy: "36", r: "36", fill: "#FFF", fillOpacity: ".95" }),
        React.createElement("path", { fill: "#444", fillRule: "nonzero", d: "M50.8350169,37.0602664 L29.4767217,49.9693832 C28.900608,50.3175908 28.1558807,50.1251285 27.8133266,49.5395068 C27.701749,49.3487566 27.6428571,49.1309436 27.6428571,48.9090213 L27.6428571,23.0907876 C27.6428571,22.4094644 28.1862113,21.8571429 28.8564727,21.8571429 C29.0747919,21.8571429 29.2890685,21.9170066 29.4767217,22.0304257 L50.8350169,34.9395425 C51.4111306,35.28775 51.6004681,36.0447682 51.257914,36.6303899 C51.154433,36.8072984 51.0090531,36.9550776 50.8350169,37.0602664 Z" }))));

var replay = (React.createElement("svg", { viewBox: "0 0 24 24" },
    React.createElement("g", { fill: "none", fillRule: "evenodd" },
        React.createElement("rect", { width: "24", height: "24" }),
        React.createElement("path", { fill: "#FFF", d: "M12.4049973,3.62840003 C17.1876069,3.84468032 21,7.87252205 21,12.8096305 C21,17.8853314 16.9705627,22 12,22 C7.02943725,22 3,17.8853314 3,12.8096305 C3,12.0229797 3.624497,11.3852732 4.39485308,11.3852732 C5.16520917,11.3852732 5.78970616,12.0229797 5.78970616,12.8096305 C5.78970616,16.3120298 8.57014942,19.1512855 12,19.1512855 C15.4298506,19.1512855 18.2102938,16.3120298 18.2102938,12.8096305 C18.2102938,9.44617464 15.6460805,6.69429107 12.4049973,6.4812447 L12.4049973,8.30324795 C12.4049973,8.71101381 12.0954942,9.04157334 11.7137028,9.04157334 C11.5841406,9.04157334 11.457186,9.00268616 11.3473174,8.92934663 L6.42999892,5.64693477 C6.10624047,5.4308193 6.00781868,4.97530916 6.21016772,4.62952441 C6.26580359,4.53445089 6.34098153,4.45415835 6.42999892,4.3947374 L11.3473174,1.11232554 C11.6710759,0.896210069 12.0975703,1.00132779 12.2999193,1.34711255 C12.3685872,1.46445578 12.4049973,1.60004749 12.4049973,1.73842422 L12.4049973,3.62840003 Z" }))));

var styles$2 = StyleSheet.create({
    container: {
        position: 'absolute',
        pointerEvents: 'none',
        overflow: 'hidden',
    },
    containerFullWidth: {
        width: '100%',
        height: 'auto',
        top: '50%',
        left: 0,
        transform: 'translate(0, -50%)',
    },
    containerFullHeight: {
        width: 'auto',
        height: '100%',
        top: 0,
        left: '50%',
        transform: 'translate(-50%)',
    },
    image: {
        display: 'block',
        visibility: 'hidden',
    },
    imageFullWidth: {
        width: '100%',
        height: 'auto',
    },
    imageFullHeight: {
        width: 'auto',
        height: '100%',
    },
    layer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
    },
});

const getContainerClassName = (isFullWidth) => css(styles$2.container, isFullWidth ? styles$2.containerFullWidth : styles$2.containerFullHeight);
const getImageClassName = (isFullWidth) => css(styles$2.image, isFullWidth ? styles$2.imageFullWidth : styles$2.imageFullHeight);
const Positioned = ({ children }) => {
    const { isFullWidth, helperImageSrc } = useContext(PositionContext);
    if (helperImageSrc) {
        return (React.createElement("div", { className: getContainerClassName(isFullWidth) },
            React.createElement("img", { src: helperImageSrc, className: getImageClassName(isFullWidth) }),
            children));
    }
    return null;
};
const Layer = ({ children }) => {
    const { objectFit } = useContext(ObjectFitContext);
    if (children) {
        const layer = React.createElement("div", { className: css(styles$2.layer) }, children);
        // 暂时先只考虑 cover
        return objectFit === 'cover' ? layer : React.createElement(Positioned, null, layer);
    }
    return null;
};

const SIZE = 64;
const BORDER = 3;
const RADIUS = (SIZE - BORDER) / 2;
const PERIMETER = 2 * Math.PI * RADIUS;
const animation = {
    '0%': {
        transform: 'rotate(0deg)',
    },
    '100%': {
        transform: 'rotate(360deg)',
    },
};
const circleAnimation = {
    '0%': {
        strokeDashoffset: PERIMETER,
        transform: 'rotate(0deg)',
    },
    '50%': {
        strokeDashoffset: PERIMETER / 4,
        transform: 'rotate(90deg)',
    },
    '100%': {
        strokeDashoffset: PERIMETER,
        transform: 'rotate(360deg)',
    },
};
var styles$1 = StyleSheet.create({
    root: {
        width: '4em',
        height: '4em',
    },
    svg: {
        display: 'block',
        width: '100%',
        height: '100%',
        animationName: animation,
        animationDuration: '2s',
        animationIterationCount: 'infinite',
        animationTimingFunction: 'linear',
    },
    circle: {
        cx: '50%',
        cy: '50%',
        r: RADIUS,
        strokeDasharray: PERIMETER,
        stroke: 'currentColor',
        strokeWidth: BORDER,
        strokeLinecap: 'round',
        fill: 'none',
        transformOrigin: 'center',
        animationName: circleAnimation,
        animationDuration: '1.5s',
        animationIterationCount: 'infinite',
        animationTimingFunction: 'ease-in-out',
    },
});

const Loader = () => {
    return (React.createElement("div", { className: css(styles$1.root) },
        React.createElement("svg", { viewBox: "0 0 64 64", className: css(styles$1.svg) },
            React.createElement("circle", { className: css(styles$1.circle) }))));
};

class MinimalTimeline extends Component {
    shouldComponentUpdate(nextProps) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.props.show || nextProps.show;
    }
    render() {
        const { duration, currentTime, buffered, progressDots } = this.props;
        return (React.createElement(Slider, { value: currentTime, total: duration, buffered: buffered, progressDots: progressDots, styles: [slider$1, minimal], noInteraction: true }));
    }
}
MinimalTimeline.defaultProps = {
    show: false,
    duration: 0,
    currentTime: 0,
    buffered: 0,
    progressDots: [],
};

const isInput = (el) => /^(input|textarea|select)$/i.test(el.tagName) || el.isContentEditable;
const usePlayerShortcuts = ({ root, prevVolumeRef, isPlaying, isPageFullScreen, volume: volume$1, duration, currentTime, standalone, onVolumeChange, onTogglePlay, onTogglePip, onToggleFullScreen, onTogglePageFullScreen, onSeek, }) => {
    const actionToastDispatch = useActionToastDispatch();
    const { playbackRates, currentPlaybackRate, setCurrentPlaybackRate } = useContext(VideoSourceContext);
    const rotatePlaybackRate = (dir) => {
        const index = playbackRates === null || playbackRates === void 0 ? void 0 : playbackRates.findIndex((x) => x.value === currentPlaybackRate.value);
        if (index >= 0) {
            const next = playbackRates[index + (dir === 'next' ? 1 : -1)];
            actionToastDispatch({
                icon: play,
                label: (next || currentPlaybackRate).text,
            });
            if (next) {
                setCurrentPlaybackRate(next);
            }
        }
    };
    const handleVolumeChange = useHandler((value, showToast = false) => {
        value = clamp(value, 0, 1);
        if (showToast) {
            actionToastDispatch({
                icon: value ? volume : muted,
                label: `${(value * 100).toFixed(0)}%`,
            });
        }
        onVolumeChange === null || onVolumeChange === void 0 ? void 0 : onVolumeChange(value);
    });
    const handleSeek = useHandler((currentTime) => {
        onSeek(clamp(currentTime, 0, duration));
    });
    const handleKeyDown = useHandler((event) => {
        // 防止事件已经在别处被处理过（如 Slider 中）
        if (event.defaultPrevented) {
            return;
        }
        // 防止冲突，有修饰键按下时不触发自定义热键
        if (event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }
        if (event.target && isInput(event.target)) {
            return;
        }
        let handled = true;
        switch (event.key) {
            case ' ':
            case 'k':
            case 'K':
                actionToastDispatch({
                    icon: isPlaying ? pause : play,
                });
                onTogglePlay();
                break;
            case 'p':
            case 'P':
                onTogglePip();
                break;
            case 't':
            case 'T':
                onTogglePageFullScreen();
                break;
            case 'f':
            case 'F':
                onToggleFullScreen();
                break;
            case 'Escape':
                if (isPageFullScreen) {
                    onTogglePageFullScreen();
                }
                break;
            case 'ArrowLeft':
                handleSeek(currentTime - 5);
                break;
            case 'ArrowRight':
                handleSeek(currentTime + 5);
                break;
            case 'ArrowUp':
                // 可能静音状态调整时不切换为非静音会更好（设置临时状态，恢复音量时应用临时状态）
                handleVolumeChange(volume$1 + 0.05, true);
                break;
            case 'ArrowDown':
                handleVolumeChange(volume$1 - 0.05, true);
                break;
            case 'j':
            case 'J':
                handleSeek(currentTime - 10);
                break;
            case 'l':
            case 'L':
                handleSeek(currentTime + 10);
                break;
            case '0':
            case '1':
            case '2':
            case '3':
            case '4':
            case '5':
            case '6':
            case '7':
            case '8':
            case '9':
                handleSeek((duration / 10) * Number(event.key));
                break;
            case 'm':
            case 'M':
                handleVolumeChange(volume$1 ? 0 : prevVolumeRef.current, true);
                break;
            case '<':
                rotatePlaybackRate('prev');
                break;
            case '>':
                rotatePlaybackRate('next');
                break;
            default:
                handled = false;
                break;
        }
        if (handled) {
            event.preventDefault();
        }
    });
    useEffect(() => {
        // 对于 React 16，需要使用 document 来处理冒泡（17 之后任何上层元素都能正常冒泡）
        if (standalone) {
            document.addEventListener('keydown', handleKeyDown);
            return () => {
                document.removeEventListener('keydown', handleKeyDown);
            };
        }
        else if (root) {
            root.addEventListener('keydown', handleKeyDown);
            return () => {
                root.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [handleKeyDown, root, standalone]);
};

const eventMap = [
    [EVENTS.PLAY, 'onPlay'],
    [EVENTS.PLAYING, 'onPlaying'],
    [EVENTS.PAUSE, 'onPause'],
    [EVENTS.ENDED, 'onEnded'],
    [EVENTS.TIMEUPDATE, 'onTimeUpdate'],
    [EVENTS.ERROR, 'onError'],
    [EVENTS.WAITING, 'onWaiting'],
    [EVENTS.CANPLAY, 'onCanPlay'],
    [EVENTS.LOADEDDATA, 'onLoadedData'],
    [EVENTS.PROGRESS, 'onProgress'],
];
function serializeDOMException(exception) {
    if (!exception)
        return null;
    const { code, message } = exception;
    return { code, message, name: exception.name };
}
function getMediaEventPayload(event) {
    const { currentTime, duration, error, videoWidth, videoHeight } = event.currentTarget;
    return {
        currentTime,
        duration,
        videoWidth,
        videoHeight,
        error: serializeDOMException(error),
    };
}
// TODO：这个文件只是做了一层方法拦截，触发播放事件，删除这些封装，简化逻辑
const VideoWithMessage = React.forwardRef((props, ref) => {
    const { updateVideoSize } = useContext(PositionContext);
    const { objectFit } = useContext(ObjectFitContext);
    const { emitEvent } = useContext(InternalMessageContext);
    const propsRef = useRef(props);
    useEffect(() => {
        propsRef.current = props;
    }, [props]);
    const newProps = useMemo(() => {
        const newProps = {};
        eventMap.forEach(([eventName, key]) => {
            newProps[key] = (event, ...args) => {
                var _a, _b;
                emitEvent(eventName, getMediaEventPayload(event));
                const handler = propsRef.current[key];
                (_b = (_a = propsRef.current).onNativeEvent) === null || _b === void 0 ? void 0 : _b.call(_a, event);
                return handler === null || handler === void 0 ? void 0 : handler(event, ...args);
            };
        });
        return newProps;
    }, [emitEvent]);
    const handleOnLoadedMetadata = useCallback((event) => {
        var _a, _b;
        const videoNode = event.currentTarget;
        if (videoNode) {
            const { videoWidth, videoHeight } = videoNode;
            updateVideoSize({ videoWidth, videoHeight });
        }
        (_b = (_a = propsRef.current).onLoadedMetadata) === null || _b === void 0 ? void 0 : _b.call(_a, event);
    }, [updateVideoSize]);
    const { Video, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onNativeEvent } = props, otherProps = __rest(props, ["Video", "onNativeEvent"]);
    return (React.createElement(Video, Object.assign({}, otherProps, newProps, { ref: ref, style: { objectFit }, onLoadedMetadata: handleOnLoadedMetadata })));
});

const NormalVideo = (props) => {
    const { onRef, 
    /* eslint-disable @typescript-eslint/no-unused-vars */
    paused, currentQuality, useAutoQuality, sources } = props, 
    /* eslint-disable @typescript-eslint/no-unused-vars */
    restProps = __rest(props, ["onRef", "paused", "currentQuality", "useAutoQuality", "sources"]);
    return React.createElement("video", Object.assign({}, restProps, { ref: onRef }));
};
var NormalVideo$1 = {
    pluginName: 'native',
    VideoComponent: NormalVideo,
    willHandleSrcChange: false,
};

function selectVideo(format, useMSE) {
    if (format === 'm3u8' &&
        (GriffithHls === null || GriffithHls === void 0 ? void 0 : GriffithHls.VideoComponent) &&
        isMSESupported() &&
        !isHlsNativeSupported()) {
        return GriffithHls;
    }
    if (format === 'mp4' &&
        (GriffithMp4 === null || GriffithMp4 === void 0 ? void 0 : GriffithMp4.VideoComponent) &&
        isMSESupported() &&
        useMSE) {
        return GriffithMp4;
    }
    return NormalVideo$1;
}
// 内部利用了 DOM 推测，避免 render 过程中多次执行
var selectVideo$1 = memoize(selectVideo, (...args) => String(args));

var styles = StyleSheet.create({
    root: {
        display: 'block',
        width: '100%',
        height: '100%',
    },
});

const { isMobile } = ua;
const isAbortError = (error) => (error && error.name === 'AbortError') ||
    (error instanceof MediaError && error.code === MediaError.MEDIA_ERR_ABORTED);
const isNotAllowedError = (error) => error && error.name === 'NotAllowedError';
class Video extends Component {
    constructor() {
        super(...arguments);
        this.isMetadataLoaded = false;
        this.pendingAction = null;
        this.loading = false;
        this.isSwitchDefinition = false;
        // refs
        this.root = null;
        /**
         * @see https://developers.google.com/web/updates/2017/06/play-request-was-interrupted
         */
        this.safeExecute = (fn) => {
            if (this.playPromise !== undefined) {
                void this.playPromise.then(() => {
                    fn();
                });
            }
            else {
                fn();
            }
        };
        this.handleMetadataLoaded = () => {
            this.isMetadataLoaded = true;
            this.applyPendingAction();
            if (this.isSwitchDefinition) {
                this.isSwitchDefinition = false;
                this.setRate(this.props.currentPlaybackRate);
                this.props.onEvent(EVENTS.CHANGE_QUALITY_SUCCESS, this.props.currentQuality);
            }
        };
        this.handleDurationChange = () => {
            const { onDurationUpdate } = this.props;
            if (onDurationUpdate) {
                onDurationUpdate(this.root.duration);
            }
        };
        this.prevLoadingEvent = undefined;
        this.handleNativeEvent = (e) => {
            var _a, _b, _c, _d;
            const type = e.type;
            if (type === 'waiting' ||
                type === 'canplay' ||
                type === 'playing' ||
                type === 'error') {
                if (type === 'waiting') {
                    this.loading = true;
                    (_b = (_a = this.props).onLoadingChange) === null || _b === void 0 ? void 0 : _b.call(_a, this.loading);
                }
                else if (
                // 修复 Safari 中可能不触发 playing 问题：https://github.com/zhihu/griffith/issues/234
                (type === 'canplay' && this.prevLoadingEvent === 'waiting') ||
                    type === 'playing' ||
                    type === 'error') {
                    this.loading = false;
                    (_d = (_c = this.props).onLoadingChange) === null || _d === void 0 ? void 0 : _d.call(_c, this.loading);
                }
                this.prevLoadingEvent = type;
            }
        };
        this.handleTimeUpdate = () => {
            this.notifyTimeUpdate(false);
        };
        // NOTE: 原生 `timeupdate` 事件更新频率不固定（4Hz~66Hz，由系统决定），这里以 rAF 提高了 UI 更新频率
        // TODO: 考虑使用回调直接操作 DOM，减少 React rendering
        this.notifyTimeUpdate = (isRaf) => {
            var _a;
            const { onCurrentTimeUpdate, paused } = this.props;
            if (this._timeUpdateRafId !== undefined) {
                window.cancelAnimationFrame(this._timeUpdateRafId);
            }
            if (paused || this.loading) {
                return;
            }
            const currentTime = (_a = this.root) === null || _a === void 0 ? void 0 : _a.currentTime;
            if (onCurrentTimeUpdate && currentTime) {
                onCurrentTimeUpdate(currentTime, isRaf);
                if (!isMobile) {
                    // 移动端使用原生进度条，不需要频繁更新，减少性能压力
                    this._timeUpdateRafId = window.requestAnimationFrame(() => this.notifyTimeUpdate(true));
                }
            }
        };
        this.handleProgress = () => {
            const { onProgressUpdate } = this.props;
            const buffered = this.root.buffered;
            const result = [];
            for (let i = 0; i < buffered.length; i++) {
                result.push({
                    start: buffered.start(i),
                    end: buffered.end(i),
                });
            }
            if (onProgressUpdate) {
                onProgressUpdate(result);
            }
        };
        this.handleError = () => {
            const { error, currentTime } = this.root || {};
            if (!error) {
                return;
            }
            const { code, message } = error;
            logger.debug('Error: %o', { code, message, name: error.name });
            const dontReportPlayFailed = isNotAllowedError(error) || isAbortError(error); // 这两种错误不认为是播放失败
            if (!dontReportPlayFailed) {
                this.props.onEvent(EVENTS.PLAY_FAILED, { currentTime });
            }
            if (this.isSwitchDefinition) {
                this.isSwitchDefinition = false;
                this.setRate(this.props.currentPlaybackRate);
                this.props.onEvent(EVENTS.CHANGE_QUALITY_FAIL, this.props.currentQuality);
            }
            this.props.onError(error);
        };
    }
    componentDidMount() {
        this.root.volume = Math.pow(this.props.volume, 2);
    }
    getSnapshotBeforeUpdate() {
        return {
            paused: this.root.paused,
            currentTime: this.root.currentTime,
        };
    }
    componentDidUpdate(prevProps, prevState, snapshot) {
        const { src, paused, volume, format, useMSE, currentPlaybackRate, currentQuality, onEvent, } = this.props;
        /**
         * 切换清晰度，如果是非 mse 视频（src 是 blob 类型）
         * data 变化的时候会 remount，所以 componentDidUpdate 中 src 变化一定是清晰度变了
         */
        if (prevProps.src && src !== prevProps.src) {
            this.isSwitchDefinition = true;
            onEvent(EVENTS.CHANGE_QUALITY_START, currentQuality);
            const { willHandleSrcChange } = selectVideo$1(format, useMSE);
            // TODO 这一块逻辑需要 Video 自己处理
            if (!willHandleSrcChange) {
                this.safeExecute(() => {
                    this.isMetadataLoaded = false;
                    this.seek(snapshot.currentTime);
                    if (!snapshot.paused) {
                        this.play();
                    }
                });
            }
        }
        if (paused !== prevProps.paused &&
            this.root &&
            paused !== this.root.paused) {
            if (paused) {
                this.pause();
            }
            else {
                this.play();
            }
        }
        if (this.root && this.root.volume !== Math.pow(volume, 2) && !isMobile) {
            this.root.volume = Math.pow(volume, 2);
        }
        if (prevProps.currentPlaybackRate.value !== currentPlaybackRate.value) {
            this.setRate(currentPlaybackRate);
        }
    }
    pending(action) {
        this.pendingAction = Object.assign(Object.assign({}, this.pendingAction), action);
    }
    applyPendingAction() {
        if (!this.pendingAction) {
            return;
        }
        const action = this.pendingAction;
        this.pendingAction = null;
        if (action.currentTime !== undefined) {
            this.seek(action.currentTime);
        }
        if (action.paused !== undefined &&
            this.root &&
            action.paused !== this.root.paused) {
            if (action.paused) {
                this.pause();
            }
            else {
                this.play();
            }
        }
    }
    setRate(rate) {
        if (!isMobile && !this.isMetadataLoaded) {
            this.pending({ paused: true });
            return;
        }
        this.safeExecute(() => (this.root.playbackRate = Number(rate.value)));
    }
    play() {
        var _a;
        if (!isMobile && !this.isMetadataLoaded) {
            this.pending({ paused: false });
            if ((_a = this.root) === null || _a === void 0 ? void 0 : _a.load) {
                this.safeExecute(() => this.root.load());
            }
            return;
        }
        this.playPromise = Promise.resolve(this.root.play()) // 保证是 promise
            .catch((error) => {
            const { onError } = this.props;
            if (onError) {
                onError(error);
            }
        });
    }
    pause() {
        if (!isMobile && !this.isMetadataLoaded) {
            this.pending({ paused: true });
            return;
        }
        this.safeExecute(() => this.root.pause());
    }
    seek(currentTime) {
        var _a;
        if (!Number.isFinite(currentTime))
            return;
        if (!this.isMetadataLoaded) {
            this.pending({ currentTime });
            return;
        }
        // see https://stackoverflow.com/a/23353005
        if ((_a = this.root) === null || _a === void 0 ? void 0 : _a.duration) {
            this.root.currentTime = currentTime;
        }
    }
    render() {
        const { src, controls, loop, paused, volume, onPlay, onPause, onEnded, onLoadedData, onSeeking, onSeeked, format, useMSE, sources, currentQuality, crossOrigin, } = this.props;
        const { VideoComponent } = selectVideo$1(format, useMSE);
        return (React.createElement(VideoWithMessage
        // @ts-expect-error ts-migrate(2322) FIXME: Type '{ onRef: (node: any) => void; className: str... Remove this comment to see the full error message
        , { 
            // @ts-expect-error ts-migrate(2322) FIXME: Type '{ onRef: (node: any) => void; className: str... Remove this comment to see the full error message
            onRef: (node) => {
                this.root = node;
            }, className: css(styles.root), preload: "metadata", playsInline: true, crossOrigin: crossOrigin, "webkit-playsinline": "", "x-webkit-airplay": "deny", muted: !volume, controls: controls, loop: loop, src: src, onPlay: onPlay, onPause: onPause, onEnded: onEnded, onLoadedData: onLoadedData, onSeeking: onSeeking, onSeeked: onSeeked, onClick: this.handleClick, onLoadedMetadata: this.handleMetadataLoaded, onError: this.handleError, onDurationChange: this.handleDurationChange, onTimeUpdate: this.handleTimeUpdate, onProgress: this.handleProgress, onNativeEvent: this.handleNativeEvent, paused: paused, sources: sources, currentQuality: currentQuality, Video: VideoComponent }));
    }
}
Video.defaultProps = {
    paused: true,
    volume: 0.5,
};
var Video$1 = React.forwardRef((props, ref) => (React.createElement(VideoSourceContext.Consumer, null, ({ currentSrc, sources, currentQuality, format, currentPlaybackRate }) => (React.createElement(Video, Object.assign({ ref: ref }, props, { src: currentSrc, format: format, sources: sources, currentQuality: currentQuality, currentPlaybackRate: currentPlaybackRate }))))));

const CONTROLLER_HIDE_DELAY = 3000;
// 非基本类型默认值（对象或数组），防止引用变化
const defaultProgressDots = [];
const InnerPlayer = ({ autoplay, muted, error, title, cover, standalone, loop, duration: durationProp = 0, onBeforePlay, useMSE, useAutoQuality, alwaysShowVolumeButton, disablePictureInPicture, progressDots = defaultProgressDots, hiddenPlayButton, hiddenTimeline, hiddenTime, hiddenQualityMenu, hiddenVolume, hiddenFullScreenButton, shouldShowPageFullScreenButton, children, hiddenPlaybackRateItem, hideMobileControls, hideCover, noWriteDocTitle, layerContent, crossOrigin, }) => {
    var _a, _b;
    const { emitEvent, subscribeAction } = useContext(InternalMessageContext);
    const { currentSrc, sources } = useContext(VideoSourceContext);
    const [root, setRoot] = useState(null);
    const videoRef = useRef();
    const actionToastDispatch = useActionToastDispatch();
    const [buffered, setBuffered] = useState([]);
    const [volume, setVolume] = useState(0.5);
    const [duration, setDuration] = useState(durationProp);
    const [currentTime, setCurrentTime] = useState(0);
    const [isDataLoaded, isDataLoadedSwitch] = useBoolean();
    const [isPlaying, isPlayingSwitch] = useBoolean();
    // 开始播放的时候设置为 true，播放中途暂停仍然为 true，直到播放到最后停止的时候才会变成 false
    const [isPlaybackStarted, isPlaybackStartedSwitch] = useBoolean();
    // 用户第一次播放之后设置为 false，并且之后永远为 false
    const [isNeverPlayed, isNeverPlayedSwitch] = useBoolean(true);
    const [isControllerShown, isControllerShownSwitch] = useBoolean();
    const [isControllerHovered, isControllerHoveredSwitch] = useBoolean();
    const [isControllerDragging, isControllerDraggingSwitch] = useBoolean();
    const [hovered, hoveredSwitch] = useBoolean();
    const [pressed, pressedSwitch] = useBoolean();
    const [isPageFullScreen, isPageFullScreenSwitch] = useBoolean();
    const [isLoading, isLoadingSwitch] = useBoolean();
    const pipRef = useRef();
    useEffect(() => {
        if (durationProp && !duration) {
            setDuration(durationProp);
        }
    }, [duration, durationProp]);
    useMount(() => {
        const historyVolume = storage.get('@griffith/history-volume');
        if (historyVolume) {
            setVolume(historyVolume);
        }
        const actionSubscriptions_ = [
            subscribeAction(ACTIONS.PLAY, handlePlay),
            subscribeAction(ACTIONS.PAUSE, handlePauseAction),
            subscribeAction(ACTIONS.TIME_UPDATE, ({ currentTime }) => handleSeek(currentTime)),
            subscribeAction(ACTIONS.SHOW_CONTROLLER, handleShowController),
            subscribeAction(ACTIONS.SET_VOLUME, ({ volume }) => handleVideoVolumeChange(volume)),
        ];
        if (videoRef.current.root) {
            if (muted) {
                handleVideoVolumeChange(0);
            }
            if (autoplay) {
                handlePlay();
            }
        }
        return () => {
            actionSubscriptions_.forEach((s) => s.unsubscribe());
        };
    });
    const showController = (() => {
        // 播放中：暂停 或 Controller shown/hovered/dragging 时展示 Controller
        if (isPlaybackStarted) {
            return (!isPlaying ||
                isControllerShown ||
                isControllerHovered ||
                isControllerDragging);
        }
        // 非播放中：播放结束时展示 Controller（未播放时不展示）
        return currentTime !== 0;
    })();
    useEffect(() => {
        if (showController) {
            emitEvent(EVENTS.SHOW_CONTROLLER);
        }
        else {
            emitEvent(EVENTS.HIDE_CONTROLLER);
        }
    }, [emitEvent, showController]);
    // sync document title
    useEffect(() => {
        if (standalone &&
            typeof title === 'string' &&
            title !== document.title &&
            !noWriteDocTitle) {
            document.title = title;
        }
    }, [noWriteDocTitle, standalone, title]);
    // setup pip
    useEffect(() => {
        if (!disablePictureInPicture && videoRef.current.root && !pipRef.current) {
            pipRef.current = new Pip();
            pipRef.current.init(videoRef.current.root, () => emitEvent(EVENTS.ENTER_PIP), () => emitEvent(EVENTS.EXIT_PIP));
        }
    }, [disablePictureInPicture, emitEvent]);
    const handlePauseAction = useHandler(({ dontApplyOnFullScreen } = {}) => {
        if (!isPlaying)
            return;
        if (dontApplyOnFullScreen && Boolean(BigScreen.element))
            return;
        handlePause();
    });
    const handleClickToTogglePlay = () => {
        // 仅点击覆盖层触发提示（控制条上的按钮点击不需要）
        actionToastDispatch({
            icon: isPlaying ? pause : play,
        });
        handleTogglePlay();
    };
    const handlePlay = useHandler(() => {
        emitEvent(EVENTS.REQUEST_PLAY);
        Promise.resolve(onBeforePlay === null || onBeforePlay === void 0 ? void 0 : onBeforePlay(currentSrc))
            .then(() => {
            if (!isPlaybackStarted) {
                emitEvent(EVENTS.PLAY_COUNT);
                isPlaybackStartedSwitch.on();
                if (!isDataLoaded) {
                    isLoadingSwitch.on();
                }
                // workaround a bug in IE about replaying a video.
                if (ua.isIE && currentTime !== 0) {
                    handleSeek(0);
                }
            }
            isPlayingSwitch.on();
            isNeverPlayedSwitch.off();
        })
            .catch(() => {
            emitEvent(EVENTS.PLAY_REJECTED);
            // 播放被取消
        });
    });
    const handlePause = useHandler(() => {
        emitEvent(EVENTS.REQUEST_PAUSE);
        isPlayingSwitch.off();
    });
    const handleVideoPlay = () => {
        if (!isPlaying) {
            isPlayingSwitch.on();
        }
    };
    const handleVideoPause = () => {
        if (isPlaying) {
            isPlayingSwitch.off();
        }
    };
    const handleVideoEnded = () => {
        isPlaybackStartedSwitch.off();
        isPlayingSwitch.off();
        isLoadingSwitch.off();
    };
    const handleVideoLoadedData = () => {
        isDataLoadedSwitch.on();
        isLoadingSwitch.off();
    };
    const handleVideoError = () => {
        isPlayingSwitch.off();
    };
    const handleVideoDurationChange = (duration) => {
        setDuration(duration);
    };
    const handleVideoTimeUpdate = (currentTime) => {
        if (isLoading || isSeekingRef.current) {
            return;
        }
        setCurrentTime(currentTime);
    };
    const handleVideoVolumeChange = useHandler((volume) => {
        volume = Math.round(volume * 100) / 100;
        setVolume(volume);
        storage.set('@griffith/history-volume', volume);
    });
    const handleSeek = useHandler((value) => {
        var _a;
        setCurrentTime(value);
        // TODO 想办法去掉这个实例方法调用
        (_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.seek(value);
    });
    const handleLoadingChange = (value) => {
        value ? isLoadingSwitch.on() : isLoadingSwitch.off();
    };
    const isSeekingRef = useRef(false);
    const handleVideoSeeking = () => {
        isSeekingRef.current = true;
    };
    const handleVideoSeeked = () => {
        isSeekingRef.current = false;
    };
    const handleVideoProgress = (value) => {
        setBuffered(value);
    };
    const handleToggleFullScreen = useHandler(() => {
        if (BigScreen.enabled) {
            const onEnter = () => {
                return emitEvent(EVENTS.ENTER_FULLSCREEN);
            };
            const onExit = () => {
                return emitEvent(EVENTS.EXIT_FULLSCREEN);
            };
            BigScreen === null || BigScreen === void 0 ? void 0 : BigScreen.toggle(root, onEnter, onExit);
        }
    });
    const handleTogglePageFullScreen = useHandler(() => {
        var _a;
        // 如果当前正在全屏就先关闭全屏
        if (Boolean(BigScreen.element) &&
            !((_a = pipRef.current) === null || _a === void 0 ? void 0 : _a.pictureInPictureElement)) {
            handleToggleFullScreen();
        }
        if (isPageFullScreen) {
            isPageFullScreenSwitch.off();
            emitEvent(EVENTS.EXIT_PAGE_FULLSCREEN);
        }
        else {
            isPageFullScreenSwitch.on();
            emitEvent(EVENTS.ENTER_PAGE_FULLSCREEN);
        }
    });
    const handleTogglePip = useHandler(() => {
        var _a;
        if (isPageFullScreen) {
            isPageFullScreenSwitch.off();
            emitEvent(EVENTS.EXIT_PAGE_FULLSCREEN);
        }
        (_a = pipRef.current) === null || _a === void 0 ? void 0 : _a.toggle();
    });
    const hideControllerTimerRef = useRef(null);
    const handleShowController = useHandler(() => {
        if (!isControllerShown) {
            isControllerShownSwitch.on();
        }
        if (hideControllerTimerRef.current !== null) {
            clearTimeout(hideControllerTimerRef.current);
        }
        hideControllerTimerRef.current = setTimeout(() => {
            hideControllerTimerRef.current = null;
            isControllerShownSwitch.off();
        }, CONTROLLER_HIDE_DELAY);
    });
    const handleHideController = () => {
        if (hideControllerTimerRef.current !== null) {
            clearTimeout(hideControllerTimerRef.current);
            hideControllerTimerRef.current = null;
        }
        isControllerShownSwitch.off();
    };
    const handleMouseEnter = () => {
        hoveredSwitch.on();
        handleShowController();
    };
    const handleMouseLeave = () => {
        hoveredSwitch.off();
        handleHideController();
    };
    const handleMouseDown = () => {
        pressedSwitch.on();
        handleShowController();
    };
    const handleMouseUp = () => {
        pressedSwitch.off();
        handleShowController();
    };
    const handleProgressDotHover = (info) => {
        emitEvent(EVENTS.HOVER_PROGRESS_DOT, info);
    };
    const handleProgressDotLeave = () => {
        emitEvent(EVENTS.LEAVE_PROGRESS_DOT);
    };
    const handleTogglePlay = useHandler(() => {
        if (isPlaying) {
            handlePause();
        }
        else {
            handlePlay();
        }
    });
    const prevVolumeRef = useRef(volume);
    const handleToggleMuted = useHandler(() => {
        if (volume) {
            prevVolumeRef.current = volume;
        }
        handleVideoVolumeChange(volume ? 0 : prevVolumeRef.current);
    });
    usePlayerShortcuts({
        root,
        prevVolumeRef,
        isPlaying,
        volume,
        currentTime,
        duration,
        standalone,
        isPageFullScreen,
        onTogglePlay: handleTogglePlay,
        onToggleFullScreen: handleToggleFullScreen,
        onTogglePageFullScreen: handleTogglePageFullScreen,
        onTogglePip: handleTogglePip,
        onVolumeChange: handleVideoVolumeChange,
        onSeek: handleSeek,
    });
    const prevSources = usePrevious(sources);
    useEffect(() => {
        if (prevSources && prevSources !== sources) {
            handleSeek(0); //TODO: Event
            handlePause();
            if (autoplay) {
                handlePlay();
            }
        }
    }, [autoplay, handlePlay, handleSeek, handlePause, prevSources, sources]);
    const isPip = Boolean((_a = pipRef.current) === null || _a === void 0 ? void 0 : _a.pictureInPictureElement);
    // Safari 会将 pip 状态视为全屏
    const isFullScreen = Boolean(BigScreen.element) && !isPip;
    const bufferedTime = useMemo(() => getBufferedTime(currentTime, buffered), [buffered, currentTime]);
    const videoDataLoaded = !isLoading || currentTime !== 0;
    const renderController = videoDataLoaded && isPlaybackStarted;
    const controlsOverlay = !ua.isMobile && (React.createElement("div", { className: css(styles$9.overlay, isNeverPlayed && styles$9.overlayMask) },
        isPlaybackStarted && isLoading && (React.createElement("div", { className: css(styles$9.loader) },
            React.createElement(Loader, null))),
        React.createElement("div", { className: css(styles$9.backdrop), onTouchStart: (event) => {
                // prevent touch to toggle
                event.preventDefault();
            }, onClick: handleClickToTogglePlay }),
        title && isFullScreen && (React.createElement("div", { className: css(styles$9.title, showController && styles$9.titleShown) }, title)),
        !hiddenTimeline && isPlaying && videoDataLoaded && (React.createElement("div", { className: css(hiddenOrShownStyle.base, showController
                ? hiddenOrShownStyle.hidden
                : hiddenOrShownStyle.shown) },
            React.createElement(MinimalTimeline, { progressDots: progressDots, buffered: bufferedTime, duration: duration, currentTime: currentTime, show: !showController }))),
        alwaysShowVolumeButton && renderController && (React.createElement("div", { className: css(styles$9.volumeButton, hiddenOrShownStyle.base, showController
                ? hiddenOrShownStyle.hidden
                : hiddenOrShownStyle.shown) },
            React.createElement(VolumeItem, { volume: volume }))),
        renderController && (React.createElement("div", { className: css(styles$9.controller, hiddenOrShownStyle.base, showController
                ? hiddenOrShownStyle.shown
                : hiddenOrShownStyle.hidden), onMouseEnter: isControllerHoveredSwitch.on, onMouseLeave: isControllerHoveredSwitch.off },
            React.createElement(Controller$1, { standalone: standalone, isPlaying: isPlaying, duration: duration, currentTime: currentTime, volume: volume, progressDots: progressDots, buffered: bufferedTime, isFullScreen: isFullScreen, isPageFullScreen: isPageFullScreen, isPip: isPip, onDragStart: isControllerDraggingSwitch.on, onDragEnd: isControllerDraggingSwitch.off, onTogglePlay: handleTogglePlay, onToggleMuted: handleToggleMuted, onSeek: handleSeek, onVolumeChange: handleVideoVolumeChange, onToggleFullScreen: handleToggleFullScreen, onTogglePageFullScreen: handleTogglePageFullScreen, onTogglePip: handleTogglePip, onProgressDotHover: handleProgressDotHover, onProgressDotLeave: handleProgressDotLeave, show: showController, showPip: !disablePictureInPicture && ((_b = pipRef.current) === null || _b === void 0 ? void 0 : _b.supported), hiddenPlayButton: hiddenPlayButton, hiddenTimeline: hiddenTimeline, hiddenTime: hiddenTime, hiddenQualityMenu: hiddenQualityMenu, hiddenVolumeItem: hiddenVolume, hiddenPlaybackRateItem: hiddenPlaybackRateItem, hiddenFullScreenButton: hiddenFullScreenButton, shouldShowPageFullScreenButton: shouldShowPageFullScreenButton })))));
    return (React.createElement("div", { className: css(styles$9.root, isFullScreen && styles$9.fullScreened, isPageFullScreen && styles$9.pageFullScreen), onMouseLeave: handleMouseLeave, onMouseEnter: handleMouseEnter, onMouseDown: handleMouseDown, onMouseUp: handleMouseUp, onMouseMove: handleShowController, ref: setRoot, tabIndex: -1, "aria-label": title },
        React.createElement("div", { className: css(styles$9.video) },
            React.createElement(Video$1, { ref: videoRef, crossOrigin: crossOrigin, controls: ua.isMobile && isPlaybackStarted && !hideMobileControls, paused: !isPlaying, volume: volume, loop: loop, onPlay: handleVideoPlay, onPause: handleVideoPause, onEnded: handleVideoEnded, onLoadedData: handleVideoLoadedData, onError: handleVideoError, onLoadingChange: handleLoadingChange, onDurationUpdate: handleVideoDurationChange, onCurrentTimeUpdate: handleVideoTimeUpdate, onSeeking: handleVideoSeeking, onSeeked: handleVideoSeeked, onProgressUpdate: handleVideoProgress, 
                // TODO: 变更 prop 名称
                onEvent: emitEvent, useMSE: useMSE, useAutoQuality: useAutoQuality })),
        hideMobileControls && isPlaybackStarted && isLoading && (React.createElement("div", { className: css(styles$9.loader) },
            React.createElement(Loader, null))),
        !hideCover && (React.createElement("div", { className: css(styles$9.cover, !isPlaybackStarted && styles$9.coverShown), onClick: () => handlePlay() },
            cover && (React.createElement(ObjectFitContext.Consumer, null, ({ objectFit }) => (React.createElement("img", { className: css(styles$9.coverImage), src: cover, style: { objectFit } })))),
            duration && currentTime === 0 && (React.createElement("div", { className: css(styles$9.coverTime, ua.isMobile && styles$9.coverTimeMobile) }, formatDuration(duration))),
            isNeverPlayed && (React.createElement("div", { className: css(styles$9.coverAction) },
                React.createElement("div", { className: css(styles$9.actionButton) },
                    React.createElement(Icon, { icon: play, styles: styles$9.actionIcon })))),
            !isNeverPlayed && currentTime !== 0 && (React.createElement("div", { className: css(styles$9.coverReplayAction) },
                React.createElement("div", { className: css(styles$9.coverReplayButton, hovered && styles$9.coverReplayButtonHovered, pressed && styles$9.coverReplayButtonPressed) },
                    React.createElement(Icon, { icon: replay, styles: styles$9.replayIcon }),
                    React.createElement(TranslatedText, { name: "replay" })))))),
        controlsOverlay,
        React.createElement(ActionToastOutlet, null),
        layerContent && React.createElement(Layer, null, layerContent),
        error && (React.createElement("div", { className: css(styles$9.error) },
            React.createElement(Icon, { icon: alert, styles: styles$9.errorIcon }),
            error.message && (React.createElement("div", { className: css(styles$9.errorMessage) }, error.message)))),
        children));
};
const DEFAULT_PLAYBACK_RATE = { value: 1.0, text: '1.0x' };
const DEFAULT_PLAYBACK_RATES = [
    { value: 0.5, text: '0.5x' },
    { value: 0.75, text: '0.75x' },
    { value: 1.0, text: '1.0x' },
    { value: 1.25, text: '1.25x' },
    { value: 1.5, text: '1.5x' },
    { value: 2.0, text: '2.0x' },
];
const Player = (_a) => {
    var { standalone, id, sources, onEvent, dispatchRef, enableCrossWindowMessage, messageContextRef, shouldObserveResize, initialObjectFit, locale = defaultLocale, localeConfig, defaultQuality, defaultQualityOrder = 'asc', defaultPlaybackRate = DEFAULT_PLAYBACK_RATE, playbackRates = DEFAULT_PLAYBACK_RATES, useAutoQuality = false } = _a, 
    // 仅解构 provider 需要的 props，其余都透传
    restProps = __rest(_a, ["standalone", "id", "sources", "onEvent", "dispatchRef", "enableCrossWindowMessage", "messageContextRef", "shouldObserveResize", "initialObjectFit", "locale", "localeConfig", "defaultQuality", "defaultQualityOrder", "defaultPlaybackRate", "playbackRates", "useAutoQuality"]);
    return (React.createElement(ObjectFitProvider, { initialObjectFit: initialObjectFit },
        React.createElement(PositionProvider, { shouldObserveResize: shouldObserveResize },
            React.createElement(MessageProvider, { id: id, enableCrossWindow: typeof enableCrossWindowMessage !== 'undefined'
                    ? enableCrossWindowMessage
                    : standalone, onEvent: onEvent, dispatchRef: dispatchRef, messageContextRef: messageContextRef },
                React.createElement(VideoSourceProvider, { sources: sources, defaultQuality: defaultQuality, useAutoQuality: useAutoQuality, defaultPlaybackRate: defaultPlaybackRate, defaultQualityOrder: defaultQualityOrder, playbackRates: playbackRates },
                    React.createElement(LocaleProvider, { locale: locale, localeConfig: localeConfig },
                        React.createElement(ActionToastProvider, null,
                            React.createElement(InnerPlayer, Object.assign({}, restProps, { useAutoQuality: useAutoQuality, standalone: standalone })))))))));
};
var Player$1 = React.memo(Player);

export { Controller$1 as Controller, Layer, LocaleProvider, MessageContext, VideoSourceContext, Player$1 as default, useMessageContextRef };
